// PixelMind.jsx — ai.degenden.xyz
// ─────────────────────────────────────────────────────────────────────────────
// PIXELMIND — Unified Image Intelligence
//
// "Upload images. See everything."
// For AI devs and researchers who need fast, private, local image analysis.
//
// One module. Two modes. Eleven tabs.
//
// VISION MODE  — deep pixel analysis on any image corpus (1 → 10,000 images)
//   UPLOAD · INSPECT · PALETTE · ENHANCE · TRANSFORM · DETECT · COMPARE
//   CLUSTER · SIMILAR · AUGMENT
//
// DOCUMENT MODE — document intelligence pipeline (1 → 20 pages)
//   UPLOAD · ENHANCE · OCR SCAN · EDITOR · EXTRACT
//
// REPORT — unified export for both modes (dark/light/sepia × full/compact/magazine)
//
// 100% in-browser. No API keys. No data leaves device. No deps beyond recharts.
//
// Architecture:
//   - Single shared image model: each item carries both vision + doc fields
//   - All engines (pHash, SSIM, fingerprint, k-means, PCA, OCR) reused verbatim
//   - Internal tab nav — no subTab prop needed
//   - Each tab gracefully adapts to current mode and loaded content
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import {
  BarChart, Bar, AreaChart, Area, ScatterChart, Scatter,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Legend,
} from 'recharts';
import { C, PALETTE, Btn, Label, ChartTip } from './wsShared';
import { startUsageSession, usageDone, usageStep, usageWorkflowFromTabs } from '../../utils/usageTracker';
import FiltersLab from '../../learning/cv/FiltersLab.jsx';
import EdgeLab from '../../learning/cv/EdgeLab.jsx';
import MorphologyLab from '../../learning/cv/MorphologyLab.jsx';
import FrequencyLab from '../../learning/cv/FrequencyLab.jsx';
import ColorSpaceLab from '../../learning/cv/ColorSpaceLab.jsx';
import FeaturesLab from '../../learning/cv/FeaturesLab.jsx';
import SegmentationLab from '../../learning/cv/SegmentationLab.jsx';
import NeuralCVLab from '../../learning/cv/NeuralCVLab.jsx';

// ═════════════════════════════════════════════════════════════════════════════
// ── 01  VISION ENGINE  (pure-JS, zero deps) ──────────────────────────────────
// ═════════════════════════════════════════════════════════════════════════════

function imgPixels(src, maxDim = 512) {
  const canvas = document.createElement('canvas');
  const isCv = src instanceof HTMLCanvasElement;
  const w0 = isCv ? src.width  : (src.naturalWidth  || src.width);
  const h0 = isCv ? src.height : (src.naturalHeight || src.height);
  const s = Math.min(1, maxDim / Math.max(w0, h0));
  const w = Math.max(1, Math.round(w0 * s)), h = Math.max(1, Math.round(h0 * s));
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext('2d'); ctx.drawImage(src, 0, 0, w, h);
  return { data: ctx.getImageData(0, 0, w, h).data, w, h, canvas, ctx, scale: s };
}

function pHash(imgEl) {
  const c = document.createElement('canvas'); c.width = 9; c.height = 8;
  const ctx = c.getContext('2d'); ctx.drawImage(imgEl, 0, 0, 9, 8);
  const d = ctx.getImageData(0, 0, 9, 8).data; let hash = '';
  for (let y = 0; y < 8; y++) for (let x = 0; x < 8; x++) {
    const i = (y*9+x)*4, j = (y*9+x+1)*4;
    hash += ((d[i]+d[i+1]+d[i+2])/3) > ((d[j]+d[j+1]+d[j+2])/3) ? '1' : '0';
  }
  return hash;
}

function hammingDist(a, b) {
  let d = 0;
  for (let i = 0; i < Math.min(a.length, b.length); i++) if (a[i] !== b[i]) d++;
  return d;
}

function ssim(imgA, imgB) {
  const { data: dA, w, h } = imgPixels(imgA, 64);
  const { data: dB } = imgPixels(imgB, 64);
  const N = w * h;
  let sA=0,sB=0,sA2=0,sB2=0,sAB=0;
  for (let i=0; i<N*4; i+=4) {
    const a=(dA[i]*.299+dA[i+1]*.587+dA[i+2]*.114)/255;
    const b=(dB[i]*.299+dB[i+1]*.587+dB[i+2]*.114)/255;
    sA+=a; sB+=b; sA2+=a*a; sB2+=b*b; sAB+=a*b;
  }
  const mA=sA/N, mB=sB/N, vA=sA2/N-mA*mA, vB=sB2/N-mB*mB, cov=sAB/N-mA*mB;
  const C1=.0001, C2=.0009;
  return +((2*mA*mB+C1)*(2*cov+C2)/((mA*mA+mB*mB+C1)*(vA+vB+C2))).toFixed(4);
}

function channelStats(imgEl) {
  const { data, w, h } = imgPixels(imgEl, 400); const N = w*h;
  const R=[],G=[],B=[],L=[];
  for (let i=0; i<data.length; i+=4) {
    R.push(data[i]); G.push(data[i+1]); B.push(data[i+2]);
    L.push(data[i]*.299+data[i+1]*.587+data[i+2]*.114);
  }
  const stat = arr => {
    const m = arr.reduce((s,v)=>s+v,0)/arr.length;
    const sd = Math.sqrt(arr.reduce((s,v)=>s+(v-m)**2,0)/arr.length);
    const sorted = [...arr].sort((a,b)=>a-b);
    return { mean:+m.toFixed(2), std:+sd.toFixed(2), min:sorted[0], max:sorted[N-1],
      p25:sorted[Math.floor(N*.25)], p75:sorted[Math.floor(N*.75)], median:sorted[Math.floor(N*.5)] };
  };
  return { R:stat(R), G:stat(G), B:stat(B), L:stat(L), pixels:N };
}

function rgbHistogram(imgEl) {
  const { data, w, h } = imgPixels(imgEl, 256);
  const bins=64, N=w*h;
  const r=new Array(bins).fill(0), g=new Array(bins).fill(0), b=new Array(bins).fill(0);
  for (let i=0; i<data.length; i+=4) {
    r[Math.floor(data[i]/4)]++; g[Math.floor(data[i+1]/4)]++; b[Math.floor(data[i+2]/4)]++;
  }
  return Array.from({length:bins},(_,i)=>({
    bin:Math.round(i*256/bins), R:r[i], G:g[i], B:b[i],
    r_pct:+(r[i]/N*100).toFixed(3), g_pct:+(g[i]/N*100).toFixed(3), b_pct:+(b[i]/N*100).toFixed(3),
  }));
}

function dominantPalette(imgEl, k=8) {
  const { data } = imgPixels(imgEl, 80);
  const pts = [];
  for (let i=0; i<data.length; i+=4) if (data[i+3]>128) pts.push([data[i],data[i+1],data[i+2]]);
  let centers = Array.from({length:k},(_,i)=>pts[Math.floor(i*pts.length/k)]);
  let labels = new Array(pts.length).fill(0);
  for (let iter=0; iter<20; iter++) {
    labels = pts.map(p => { let best=0,bestD=Infinity; centers.forEach((c,ci)=>{ const d=(p[0]-c[0])**2+(p[1]-c[1])**2+(p[2]-c[2])**2; if(d<bestD){bestD=d;best=ci;} }); return best; });
    const sums = Array.from({length:k},()=>[0,0,0,0]);
    labels.forEach((l,i)=>{ sums[l][0]+=pts[i][0]; sums[l][1]+=pts[i][1]; sums[l][2]+=pts[i][2]; sums[l][3]++; });
    centers = sums.map((s,ci)=>s[3]>0?[s[0]/s[3],s[1]/s[3],s[2]/s[3]]:centers[ci]);
  }
  const counts = new Array(k).fill(0); labels.forEach(l=>counts[l]++);
  const total = pts.length||1;
  return centers.map((c,i)=>({
    r:Math.round(c[0]), g:Math.round(c[1]), b:Math.round(c[2]),
    hex:`#${[c[0],c[1],c[2]].map(v=>Math.round(v).toString(16).padStart(2,'0')).join('')}`,
    pct:+(counts[i]/total*100).toFixed(1), count:counts[i],
    hsl:rgbToHsl(Math.round(c[0]),Math.round(c[1]),Math.round(c[2])),
  })).sort((a,b)=>b.pct-a.pct);
}

function rgbToHsl(r,g,b) {
  r/=255; g/=255; b/=255;
  const max=Math.max(r,g,b), min=Math.min(r,g,b); let h,s, l=(max+min)/2;
  if(max===min){h=s=0;}else{
    const d=max-min; s=l>.5?d/(2-max-min):d/(max+min);
    switch(max){case r:h=(g-b)/d+(g<b?6:0);break;case g:h=(b-r)/d+2;break;default:h=(r-g)/d+4;}
    h/=6;
  }
  return {h:Math.round(h*360),s:Math.round(s*100),l:Math.round(l*100)};
}

function fingerprint(imgEl) {
  const { data } = imgPixels(imgEl, 32); const feat = [];
  for (let by=0; by<8; by++) for (let bx=0; bx<8; bx++) {
    let sr=0,sg=0,sb=0,n=0;
    for (let dy=0; dy<4; dy++) for (let dx=0; dx<4; dx++) {
      const idx=((by*4+dy)*32+(bx*4+dx))*4;
      sr+=data[idx]; sg+=data[idx+1]; sb+=data[idx+2]; n++;
    }
    feat.push(sr/n/255, sg/n/255, sb/n/255);
  }
  return feat;
}

function clusterImages(fps, k=5, iters=30) {
  const n=fps.length; if(n<k)k=n; const dim=fps[0].length;
  const centers=[fps[Math.floor(Math.random()*n)]];
  while(centers.length<k){
    const dists=fps.map(fp=>{let m=Infinity;centers.forEach(c=>{const d=fp.reduce((s,v,i)=>s+(v-c[i])**2,0);if(d<m)m=d;});return m;});
    const sum=dists.reduce((s,d)=>s+d,0); let r=Math.random()*sum,cu=0;
    for(let i=0;i<n;i++){cu+=dists[i];if(cu>=r){centers.push(fps[i]);break;}}
  }
  let labels=new Array(n).fill(0);
  for(let iter=0;iter<iters;iter++){
    labels=fps.map(fp=>{let best=0,bestD=Infinity;centers.forEach((c,ci)=>{const d=fp.reduce((s,v,i)=>s+(v-c[i])**2,0);if(d<bestD){bestD=d;best=ci;}});return best;});
    const sums=Array.from({length:k},()=>({s:new Array(dim).fill(0),n:0}));
    fps.forEach((fp,i)=>{const l=labels[i];fp.forEach((v,d)=>{sums[l].s[d]+=v;});sums[l].n++;});
    sums.forEach((s,ci)=>{if(s.n>0)centers[ci]=s.s.map(v=>v/s.n);});
  }
  return labels;
}

function pca2d(vecs) {
  if(vecs.length<2)return vecs.map((_,i)=>({x:i,y:0}));
  const n=vecs.length, d=vecs[0].length;
  const means=Array.from({length:d},(_,j)=>vecs.reduce((s,v)=>s+v[j],0)/n);
  const centered=vecs.map(v=>v.map((x,j)=>x-means[j]));
  function powerIter(X,iters=30){
    let u=Array.from({length:d},()=>Math.random()-.5);
    for(let it=0;it<iters;it++){
      let Au=new Array(d).fill(0);
      X.forEach(x=>{const dot=x.reduce((s,v,j)=>s+v*u[j],0);x.forEach((v,j)=>{Au[j]+=dot*v;});});
      const norm=Math.sqrt(Au.reduce((s,v)=>s+v*v,0))||1; u=Au.map(v=>v/norm);
    }
    return u;
  }
  const pc1=powerIter(centered);
  const proj1=centered.map(x=>x.reduce((s,v,j)=>s+v*pc1[j],0));
  const deflated=centered.map((x,i)=>x.map((v,j)=>v-proj1[i]*pc1[j]));
  const pc2=powerIter(deflated);
  const proj2=centered.map(x=>x.reduce((s,v,j)=>s+v*pc2[j],0));
  return vecs.map((_,i)=>({x:+proj1[i].toFixed(4),y:+proj2[i].toFixed(4)}));
}

function cosineSim(a, b) {
  const dot=a.reduce((s,v,i)=>s+v*b[i],0);
  const na=Math.sqrt(a.reduce((s,v)=>s+v*v,0)), nb=Math.sqrt(b.reduce((s,v)=>s+v*v,0));
  return na&&nb?+(dot/(na*nb)).toFixed(4):0;
}

function sharpness(imgEl) {
  const { data, w, h } = imgPixels(imgEl, 256); const lap=[];
  for(let y=1;y<h-1;y++) for(let x=1;x<w-1;x++){
    const i=(y*w+x)*4, l=(data[i]*.299+data[i+1]*.587+data[i+2]*.114);
    const n=((y-1)*w+x)*4, s=((y+1)*w+x)*4, e=(y*w+x+1)*4, we=(y*w+x-1)*4;
    const nL=data[n]*.299+data[n+1]*.587+data[n+2]*.114;
    const sL=data[s]*.299+data[s+1]*.587+data[s+2]*.114;
    const eL=data[e]*.299+data[e+1]*.587+data[e+2]*.114;
    const wL=data[we]*.299+data[we+1]*.587+data[we+2]*.114;
    lap.push(Math.abs(4*l-nL-sL-eL-wL));
  }
  const m=lap.reduce((s,v)=>s+v,0)/lap.length;
  return +(lap.reduce((s,v)=>s+(v-m)**2,0)/lap.length).toFixed(2);
}

function perceptualMetrics(imgEl) {
  const { data } = imgPixels(imgEl, 128);
  let sumL=0,sumL2=0,n=0;
  for(let i=0;i<data.length;i+=4){
    const l=(data[i]*.299+data[i+1]*.587+data[i+2]*.114)/255;
    sumL+=l; sumL2+=l*l; n++;
  }
  const meanL=sumL/n, rms=Math.sqrt(sumL2/n-meanL**2);
  return { brightness:+(meanL*100).toFixed(1), contrast:+(rms*100).toFixed(1), sharpness:sharpness(imgEl) };
}

function detectHarmony(palette) {
  if(!palette?.length)return null;
  const hues=palette.map(c=>c.hsl.h);
  const diffs=hues.map((h)=>Math.abs(h-hues[0]));
  const hasComp=diffs.some(d=>d>155&&d<205), hasTri=diffs.filter(d=>(d>105&&d<135)||(d>225&&d<255)).length>=2;
  const hasAna=diffs.filter(d=>d<45||d>315).length>=3, allNeu=palette.every(c=>c.hsl.s<15);
  if(allNeu)return{name:'MONOCHROMATIC',desc:'All desaturated tones — minimal, elegant',color:C.t3};
  if(hasTri) return{name:'TRIADIC',desc:'Three evenly-spaced hues — vibrant, balanced',color:C.violet};
  if(hasComp)return{name:'COMPLEMENTARY',desc:'Opposing hues — high contrast, energetic',color:C.orange};
  if(hasAna) return{name:'ANALOGOUS',desc:'Adjacent hues — cohesive, natural',color:C.green};
  return{name:'COMPLEX',desc:'Multi-hue mix — rich, diverse palette',color:C.cyan};
}

// ─── Advanced quality metrics ─────────────────────────────────────────────────

// Shannon entropy of luminance histogram (bits, max ≈ 8)
function imageEntropy(imgEl) {
  const { data, w, h } = imgPixels(imgEl, 128);
  const hist = new Array(256).fill(0);
  const N = w * h;
  for (let i = 0; i < data.length; i += 4) {
    const l = Math.round(data[i] * .299 + data[i+1] * .587 + data[i+2] * .114);
    hist[Math.min(255, Math.max(0, l))]++;
  }
  let entropy = 0;
  for (let i = 0; i < 256; i++) {
    if (hist[i] === 0) continue;
    const p = hist[i] / N;
    entropy -= p * Math.log2(p);
  }
  return +entropy.toFixed(3);
}

// Noise estimate: std-dev of (pixel - local 3×3 mean) — high = noisy
function noiseEstimate(imgEl) {
  const { data, w, h } = imgPixels(imgEl, 128);
  let sumDiff2 = 0, n = 0;
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const i = (y * w + x) * 4;
      const l = data[i] * .299 + data[i+1] * .587 + data[i+2] * .114;
      let sum = 0;
      for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
        const j = ((y+dy) * w + (x+dx)) * 4;
        sum += data[j] * .299 + data[j+1] * .587 + data[j+2] * .114;
      }
      sumDiff2 += (l - sum / 9) ** 2; n++;
    }
  }
  return +(Math.sqrt(sumDiff2 / (n || 1)) / 2.55).toFixed(2);
}

// Approximate color temperature from R/B ratio
function colorTemp(imgEl) {
  const { data } = imgPixels(imgEl, 64);
  let sumR = 0, sumB = 0, n = data.length / 4;
  for (let i = 0; i < data.length; i += 4) { sumR += data[i]; sumB += data[i+2]; }
  const rb = sumR / (sumB || 1);
  if (rb > 1.4)  return { k: 2800, label: 'WARM',          color: '#fb923c' };
  if (rb > 1.15) return { k: 3500, label: 'NEUTRAL WARM',  color: '#fbbf24' };
  if (rb > 0.9)  return { k: 5500, label: 'DAYLIGHT',      color: '#e2e8f0' };
  if (rb > 0.75) return { k: 6500, label: 'COOL',          color: '#60a5fa' };
  return                { k: 8000, label: 'COLD',           color: '#22d3ee' };
}

// Exposure clipping: % pixels at crushed black or blown highlight
function exposureClipping(imgEl) {
  const { data, w, h } = imgPixels(imgEl, 128);
  const N = w * h;
  let shadows = 0, highlights = 0;
  for (let i = 0; i < data.length; i += 4) {
    const l = data[i] * .299 + data[i+1] * .587 + data[i+2] * .114;
    if (l <= 5)   shadows++;
    if (l >= 250) highlights++;
  }
  return {
    shadows:    +(shadows    / N * 100).toFixed(1),
    highlights: +(highlights / N * 100).toFixed(1),
    clipped:    +((shadows + highlights) / N * 100).toFixed(1),
  };
}

// Composite quality score 0–100
function qualityScore(blurVar, clippedPct, noiseLvl) {
  const blurNorm  = Math.min(100, Math.log1p(blurVar)   / Math.log1p(8000) * 100);
  const clipNorm  = Math.max(0, 100 - clippedPct * 2);
  const noiseNorm = Math.max(0, 100 - noiseLvl  * 2);
  return +((blurNorm * .5 + clipNorm * .3 + noiseNorm * .2)).toFixed(1);
}

// ─── Canvas filter pipeline ───────────────────────────────────────────────────
function applyCanvasFilter(imgEl, filters) {
  const c=document.createElement('canvas');
  c.width=imgEl.naturalWidth||imgEl.width; c.height=imgEl.naturalHeight||imgEl.height;
  const ctx=c.getContext('2d'); ctx.drawImage(imgEl,0,0);
  const id=ctx.getImageData(0,0,c.width,c.height); const d=id.data;
  const out=new Uint8ClampedArray(d);
  const {brightness=1,contrast=1,saturation=1,hue=0,temperature=0,
    grayscale=false,invert=false,threshold=0,sepia=false,sharpen=false,blur=false,edges=false}=filters;
  for(let i=0;i<d.length;i+=4){
    let r=d[i],g=d[i+1],b=d[i+2];
    r*=brightness; g*=brightness; b*=brightness;
    const ic=128*(1-contrast); r=r*contrast+ic; g=g*contrast+ic; b=b*contrast+ic;
    r+=temperature*20; b-=temperature*15;
    if(saturation!==1){const gray=.299*r+.587*g+.114*b;r=gray+(r-gray)*saturation;g=gray+(g-gray)*saturation;b=gray+(b-gray)*saturation;}
    if(hue!==0){const rad=hue*Math.PI/180,cos=Math.cos(rad),sin=Math.sin(rad);const nr=r*(cos+.143*(1-cos)+.140*sin)+g*(.143*(1-cos)-.140*sin)+b*(.143*(1-cos)+.283*sin);const ng=r*(.143*(1-cos)+.140*sin)+g*(cos+.140*(1-cos))+b*(.140*(1-cos)-.283*sin);const nb=r*(.143*(1-cos)-.283*sin)+g*(.140*(1-cos)+.283*sin)+b*(cos+.140*(1-cos));r=nr;g=ng;b=nb;}
    if(sepia){const nr=r*.393+g*.769+b*.189,ng=r*.349+g*.686+b*.168,nb=r*.272+g*.534+b*.131;r=nr;g=ng;b=nb;}
    if(grayscale){const gray=.299*r+.587*g+.114*b;r=g=b=gray;}
    if(threshold>0){const gray=.299*r+.587*g+.114*b;const bin=gray>threshold?255:0;r=g=b=bin;}
    if(invert){r=255-r;g=255-g;b=255-b;}
    out[i]=Math.min(255,Math.max(0,r));out[i+1]=Math.min(255,Math.max(0,g));out[i+2]=Math.min(255,Math.max(0,b));out[i+3]=d[i+3];
  }
  const doConv=(kernel,w,h)=>{const conv=new Uint8ClampedArray(out);for(let y=1;y<h-1;y++)for(let x=1;x<w-1;x++){let rr=0,gg=0,bb=0;for(let ky=-1;ky<=1;ky++)for(let kx=-1;kx<=1;kx++){const idx=((y+ky)*w+(x+kx))*4,k=kernel[(ky+1)*3+(kx+1)];rr+=out[idx]*k;gg+=out[idx+1]*k;bb+=out[idx+2]*k;}const oi=(y*w+x)*4;conv[oi]=Math.min(255,Math.max(0,rr));conv[oi+1]=Math.min(255,Math.max(0,gg));conv[oi+2]=Math.min(255,Math.max(0,bb));conv[oi+3]=out[oi+3];}return conv;};
  let final=out;
  if(sharpen)final=doConv([0,-1,0,-1,5,-1,0,-1,0],c.width,c.height);
  if(blur)   final=doConv([1/9,1/9,1/9,1/9,1/9,1/9,1/9,1/9,1/9],c.width,c.height);
  if(edges)  final=doConv([-1,-1,-1,-1,8,-1,-1,-1,-1],c.width,c.height);
  ctx.putImageData(new ImageData(final,c.width,c.height),0,0);
  return c.toDataURL('image/png');
}

// ═════════════════════════════════════════════════════════════════════════════
// ── 02  DOCUMENT ENGINE  (OCR + extraction) ──────────────────────────────────
// ═════════════════════════════════════════════════════════════════════════════

const RX = {
  currency:/(?:USD|EUR|GBP|INR|JPY|CAD|AUD)?\s*[\$€£₹¥]\s*[\d,]+(?:\.\d{1,2})?|\b[\d,]+(?:\.\d{2})?\s*(?:USD|EUR|GBP|INR|JPY)/gi,
  percent:/\b\d+(?:\.\d+)?\s*%/g,
  dateYMD:/\b\d{4}[-/.]\d{1,2}[-/.]\d{1,2}\b/g,
  dateDMY:/\b\d{1,2}[-/.]\d{1,2}[-/.]\d{2,4}\b/g,
  dateWritten:/\b(?:jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:tember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\s+\d{1,2}(?:st|nd|rd|th)?,?\s*\d{2,4}/gi,
  email:/\b[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}\b/g,
  phone:/(?:\+?\d{1,3}[-.\ ]?)?\(?\d{3}\)?[-.\ ]?\d{3}[-.\ ]?\d{4}/g,
  url:/https?:\/\/[^\s"'<>]+/g,
  invoice:/(?:invoice|inv|bill|receipt)\s*(?:no\.?|number|#)?\s*[:\-]?\s*([A-Z0-9\-]{3,20})/gi,
  orderNum:/(?:order|po|purchase order)\s*(?:no\.?|number|#)?\s*[:\-]?\s*([A-Z0-9\-]{3,20})/gi,
  totalLine:/(?:total|amount due|grand total|balance|subtotal)[:\s]+(?:USD|EUR|GBP|INR|JPY|CAD|AUD)?[\$€£₹¥]?\s*([\d,]+(?:\.\d{2})?)/gi,
};

const ENTITY_PATTERNS=[
  {type:'AMOUNT', rx:RX.currency,    color:'#4ade80',icon:'💰'},
  {type:'PERCENT',rx:RX.percent,     color:'#fbbf24',icon:'📊'},
  {type:'DATE',   rx:RX.dateYMD,     color:'#22d3ee',icon:'📅'},
  {type:'DATE',   rx:RX.dateDMY,     color:'#22d3ee',icon:'📅'},
  {type:'DATE',   rx:RX.dateWritten, color:'#22d3ee',icon:'📅'},
  {type:'EMAIL',  rx:RX.email,       color:'#f472b6',icon:'📧'},
  {type:'PHONE',  rx:RX.phone,       color:'#a78bfa',icon:'📞'},
  {type:'URL',    rx:RX.url,         color:'#60a5fa',icon:'🔗'},
  {type:'INVOICE#',rx:RX.invoice,    color:'#fb923c',icon:'🧾'},
  {type:'ORDER#', rx:RX.orderNum,    color:'#fb923c',icon:'📦'},
  {type:'TOTAL',  rx:RX.totalLine,   color:'#4ade80',icon:'💵'},
];

function extractStructured(text) {
  const found = {};
  ENTITY_PATTERNS.forEach(({type,rx,color,icon})=>{
    const rxC=new RegExp(rx.source,rx.flags); let m;
    while((m=rxC.exec(text))!==null){
      const val=(m[1]||m[0]).trim();
      if(!val||val.length<2)continue;
      if(!found[type])found[type]={type,color,icon,values:[]};
      if(!found[type].values.includes(val))found[type].values.push(val);
    }
  });
  return Object.values(found);
}

function extractTables(text) {
  const lines=text.split('\n').map(l=>l.trim()).filter(Boolean);
  const tables=[]; let current=[];
  const isRow=l=>['\\t','|','  '].some(d=>l.split(d).length>=2)&&l.length>8;
  lines.forEach((line,i)=>{
    if(isRow(line)){current.push(line);}
    else{
      if(current.length>=2){
        const sep=current[0].includes('\t')?'\t':current[0].includes('|')?'|':/\s{2,}/;
        const rows=current.map(l=>l.split(sep).map(c=>c.trim()).filter(Boolean));
        if(Math.max(...rows.map(r=>r.length))>=2)tables.push({rows,startLine:i-current.length,rawLines:current});
      }
      current=[];
    }
  });
  if(current.length>=2){
    const sep=current[0].includes('\t')?'\t':current[0].includes('|')?'|':/\s{2,}/;
    const rows=current.map(l=>l.split(sep).map(c=>c.trim()).filter(Boolean));
    if(Math.max(...rows.map(r=>r.length))>=2)tables.push({rows,rawLines:current});
  }
  return tables;
}

function docStats(pages) {
  const allText=pages.map(p=>p.text||'').join('\n');
  const words=allText.split(/\s+/).filter(Boolean);
  const lines=allText.split('\n').filter(Boolean);
  const chars=allText.replace(/\s/g,'').length;
  const readTime=Math.ceil(words.length/200);
  const avgConf=pages.length?+(pages.reduce((a,p)=>a+(p.confidence||0),0)/pages.length).toFixed(1):0;
  return {words:words.length,lines:lines.length,chars,readTime,avgConf,pages:pages.length};
}

function applyDocEnhancements(imgEl, opts) {
  const {brightness=1,contrast=1,threshold=0,sharpen=false,grayscale=false,invert=false}=opts;
  const canvas=document.createElement('canvas');
  const W=imgEl.naturalWidth||imgEl.width, H=imgEl.naturalHeight||imgEl.height;
  canvas.width=W; canvas.height=H;
  const ctx=canvas.getContext('2d'); ctx.drawImage(imgEl,0,0,W,H);
  const id=ctx.getImageData(0,0,W,H); const d=id.data; const out=new Uint8ClampedArray(d.length);
  for(let i=0;i<d.length;i+=4){
    let r=d[i],g=d[i+1],b=d[i+2];
    if(grayscale||threshold>0){const gray=.299*r+.587*g+.114*b;r=g=b=gray;}
    r*=brightness;g*=brightness;b*=brightness;
    const f=contrast,ic=128*(1-f); r=r*f+ic;g=g*f+ic;b=b*f+ic;
    if(threshold>0){const gray2=.299*r+.587*g+.114*b;const bin=gray2>threshold?255:0;r=g=b=bin;}
    if(invert){r=255-r;g=255-g;b=255-b;}
    out[i]=Math.min(255,Math.max(0,r));out[i+1]=Math.min(255,Math.max(0,g));out[i+2]=Math.min(255,Math.max(0,b));out[i+3]=d[i+3];
  }
  if(sharpen){
    const k=[0,-1,0,-1,5,-1,0,-1,0]; const sh=new Uint8ClampedArray(out.length);
    for(let y=1;y<H-1;y++)for(let x=1;x<W-1;x++){
      let rr=0,gg=0,bb=0;
      for(let ky=-1;ky<=1;ky++)for(let kx=-1;kx<=1;kx++){const idx=((y+ky)*W+(x+kx))*4,kv=k[(ky+1)*3+(kx+1)];rr+=out[idx]*kv;gg+=out[idx+1]*kv;bb+=out[idx+2]*kv;}
      const oi=(y*W+x)*4; sh[oi]=Math.min(255,Math.max(0,rr));sh[oi+1]=Math.min(255,Math.max(0,gg));sh[oi+2]=Math.min(255,Math.max(0,bb));sh[oi+3]=out[oi+3];
    }
    ctx.putImageData(new ImageData(sh,W,H),0,0);
  } else { ctx.putImageData(new ImageData(out,W,H),0,0); }
  return canvas.toDataURL('image/png');
}

// ─── Lazy CDN loaders ─────────────────────────────────────────────────────────
const CDN={};
function loadScript(url,key){
  if(CDN[key])return CDN[key];
  CDN[key]=new Promise((res,rej)=>{
    if(window[key]){res(window[key]);return;}
    const s=document.createElement('script');s.src=url;s.async=true;
    s.onload=()=>res(window[key]);s.onerror=()=>rej(new Error(`Failed: ${url}`));
    document.head.appendChild(s);
  });
  return CDN[key];
}
const getTF=()=>loadScript('https://cdn.jsdelivr.net/npm/@tensorflow/tfjs@4.15.0/dist/tf.min.js','tf');
const getCocoSsd=async()=>{await getTF();return loadScript('https://cdn.jsdelivr.net/npm/@tensorflow-models/coco-ssd@2.2.3/dist/coco-ssd.min.js','cocoSsd');};
const getMobileNet=async()=>{await getTF();return loadScript('https://cdn.jsdelivr.net/npm/@tensorflow-models/mobilenet@2.1.0/dist/mobilenet.min.js','mobilenet');};
let _tess=null;
async function getTesseract(){
  if(_tess)return _tess;
  return new Promise((res,rej)=>{
    if(window.Tesseract){_tess=window.Tesseract;res(_tess);return;}
    const s=document.createElement('script');
    s.src='https://cdnjs.cloudflare.com/ajax/libs/tesseract.js/4.1.1/tesseract.min.js';
    s.onload=()=>{_tess=window.Tesseract;res(_tess);};
    s.onerror=()=>rej(new Error('Tesseract CDN load failed'));
    document.head.appendChild(s);
  });
}

// ═════════════════════════════════════════════════════════════════════════════
// ── 03  SHARED UI ATOMS ──────────────────────────────────────────────────────
// ═════════════════════════════════════════════════════════════════════════════

const Card   = ({children,style={}})=><div style={{background:C.surface,border:`1px solid ${C.b1}`,borderRadius:4,overflow:'hidden',...style}}>{children}</div>;
const Head   = ({label,accent=C.cyan,right,sub})=>(
  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'9px 14px',background:C.deep,borderBottom:`1px solid ${C.b0}`}}>
    <div>
      <div style={{fontSize:9,color:accent,letterSpacing:'.13em',fontWeight:700,fontFamily:'JetBrains Mono,monospace'}}>{label}</div>
      {sub&&<div style={{fontSize:8,color:C.t4,marginTop:1}}>{sub}</div>}
    </div>
    {right}
  </div>
);
const Body  = ({children,style={}})=><div style={{padding:14,...style}}>{children}</div>;
const MB    = ({label,value,color=C.cyan,sub,onClick})=>(
  <div onClick={onClick} style={{background:C.raised,border:`1px solid ${color}22`,borderLeft:`2px solid ${color}`,padding:'9px 13px',borderRadius:3,cursor:onClick?'pointer':'default'}}>
    <div style={{fontSize:8,color:C.t4,fontFamily:'JetBrains Mono,monospace',letterSpacing:'.1em'}}>{label}</div>
    <div style={{fontFamily:'Syne,sans-serif',fontWeight:800,fontSize:18,color,lineHeight:1.1}}>{value}</div>
    {sub&&<div style={{fontSize:9,color:C.t3,marginTop:2}}>{sub}</div>}
  </div>
);
const Info  = ({children,color=C.cyan})=><div style={{padding:'10px 13px',background:color+'10',border:`1px solid ${color}33`,borderRadius:3,fontSize:11,color:C.t2,lineHeight:1.7}}>{children}</div>;
const MBar  = ({pct,color=C.cyan,h=4})=><div style={{height:h,background:C.b1,borderRadius:2,overflow:'hidden'}}><div style={{height:'100%',width:`${Math.min(100,pct)}%`,background:color,borderRadius:2}}/></div>;
const TH    = {padding:'6px 10px',textAlign:'left',fontSize:9,color:C.t3,letterSpacing:'.09em',borderBottom:`1px solid ${C.b0}`,fontFamily:'JetBrains Mono,monospace',whiteSpace:'nowrap'};
const TD    = {padding:'6px 10px',color:'#b6c3d8',fontFamily:'JetBrains Mono,monospace',fontSize:10};

const ConfBadge = ({conf})=>{
  const color=conf>=80?C.green:conf>=60?C.gold:C.red;
  return <span style={{fontSize:8,padding:'2px 7px',border:`1px solid ${color}55`,color,borderRadius:2,fontFamily:'JetBrains Mono,monospace',fontWeight:700}}>{conf?.toFixed(0)||'—'}%</span>;
};

const Swatch = ({hex,pct,label,size=32})=>(
  <div style={{display:'flex',alignItems:'center',gap:8,padding:'5px 0',borderBottom:`1px solid ${C.b0}`}}>
    <div style={{width:size,height:size,borderRadius:4,background:hex,flexShrink:0,boxShadow:'0 2px 6px rgba(0,0,0,.4)'}}/>
    <div style={{flex:1}}>
      <div style={{fontFamily:'JetBrains Mono,monospace',fontSize:10,color:C.t1,fontWeight:700}}>{hex.toUpperCase()}</div>
      {label&&<div style={{fontSize:9,color:C.t3}}>{label}</div>}
    </div>
    {pct!=null&&<div>
      <div style={{fontFamily:'Syne,sans-serif',fontWeight:700,fontSize:13,color:C.t1,textAlign:'right'}}>{pct}%</div>
      <div style={{width:60,marginTop:2}}><MBar pct={pct} color={hex}/></div>
    </div>}
  </div>
);

const ImgThumb = ({img,selected,onClick,size=72,label,badge,badgeColor=C.cyan})=>(
  <div onClick={onClick} style={{position:'relative',cursor:'pointer',flexShrink:0,width:size,borderRadius:3,overflow:'hidden',border:`2px solid ${selected?C.cyan:C.b1}`,transition:'border-color .12s',background:C.deep}}>
    <img src={img.processedUrl||img.url} alt={img.name} style={{width:'100%',height:size*1.2,objectFit:'cover',display:'block'}}/>
    {badge!=null&&<div style={{position:'absolute',top:3,right:3,background:'rgba(6,11,20,.9)',borderRadius:2,padding:'1px 5px',fontSize:7,color:badgeColor,fontFamily:'JetBrains Mono,monospace',fontWeight:700}}>{badge}</div>}
    {label&&<div style={{position:'absolute',bottom:0,left:0,right:0,background:'rgba(6,11,20,.85)',padding:'2px 4px',fontSize:7,color:C.t3,fontFamily:'JetBrains Mono,monospace',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{label}</div>}
  </div>
);

const SliderRow = ({label,value,min,max,step=0.05,onChange,displayVal,color=C.cyan})=>(
  <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:8}}>
    <span style={{fontSize:9,color:C.t3,fontFamily:'JetBrains Mono,monospace',minWidth:90}}>{label}</span>
    <input type="range" min={min} max={max} step={step} value={value} onChange={e=>onChange(+e.target.value)} style={{flex:1,accentColor:color,height:3}}/>
    <span style={{fontSize:9,color,fontFamily:'JetBrains Mono,monospace',minWidth:38,textAlign:'right',fontWeight:700}}>{displayVal??value}</span>
  </div>
);

const PageThumb = ({img, selected, onClick, size=72})=>(
  <div onClick={onClick} style={{position:'relative',cursor:'pointer',flexShrink:0,width:size,borderRadius:3,overflow:'hidden',border:`2px solid ${selected?C.cyan:C.b1}`,transition:'border-color .15s',background:C.deep}}>
    <img src={img.processedUrl||img.url} alt={img.name} style={{width:'100%',height:size*1.35,objectFit:'cover',display:'block'}}/>
    <div style={{position:'absolute',bottom:0,left:0,right:0,background:'rgba(6,11,20,.85)',padding:'2px 4px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
      <span style={{fontSize:7,color:C.t3,fontFamily:'JetBrains Mono,monospace'}}>p{img.pageNum||'—'}</span>
      {img.confidence!=null&&<ConfBadge conf={img.confidence}/>}
    </div>
  </div>
);

// Mode badge pill
const ModeBadge = ({mode})=>{
  const cfg={vision:{c:C.cyan,icon:'🔬',label:'VISION'},doc:{c:C.violet,icon:'📄',label:'DOCUMENT'}};
  const {c,icon,label}=cfg[mode]||cfg.vision;
  return <span style={{fontSize:8,padding:'2px 8px',background:c+'14',border:`1px solid ${c}44`,borderRadius:20,color:c,fontFamily:'JetBrains Mono,monospace',fontWeight:700}}>{icon} {label}</span>;
};

// ═════════════════════════════════════════════════════════════════════════════
// ── 04  UPLOAD TAB ───────────────────────────────────────────────────────────
// ═════════════════════════════════════════════════════════════════════════════

function UploadTab({ images, onImagesChange, mode, onModeChange }) {
  const [isDrag, setIsDrag] = useState(false);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [renaming, setRenaming] = useState(null);
  const [renameVal, setRenameVal] = useState('');
  const inputRef = useRef();
  const MAX_VISION = 10000, MAX_DOC = 20;

  const processFiles = useCallback(async files => {
    const valid = Array.from(files).filter(f => f.type.startsWith('image/'));
    if (!valid.length) return;
    const startingFresh = images.length === 0;
    setLoading(true);
    const MAX = mode === 'doc' ? MAX_DOC : MAX_VISION;
    const toAdd = valid.slice(0, MAX - images.length);
    const results = [];
    for (let i = 0; i < toAdd.length; i++) {
      const file = toAdd[i];
      setProgress(Math.round(i / toAdd.length * 100));
      await new Promise(r => setTimeout(r, 0));
      const url = URL.createObjectURL(file);
      await new Promise(res => {
        const img = new Image();
        img.onload = () => {
          const base = {
            id: Date.now() + Math.random(),
            name: file.name.replace(/\.[^.]+$/, ''),
            url, file,
            width: img.naturalWidth, height: img.naturalHeight,
            fileSize: +(file.size / 1024).toFixed(1),
            fileType: file.type,
            mode,
            processedUrl: null,
          };
          if (mode === 'vision') {
            const fp = fingerprint(img);
            const hash = pHash(img);
            const metrics = perceptualMetrics(img);
            results.push({ ...base, fp, hash, metrics, palette: null, labels: null, objects: null });
          } else {
            results.push({
              ...base,
              pageNum: images.length + results.length + 1,
              status: 'ready',
              enhancements: { brightness: 1, contrast: 1, threshold: 0, sharpen: false, grayscale: false, invert: false },
              text: '', confidence: null, entities: [], tables: [],
            });
          }
          res();
        };
        img.onerror = res;
        img.src = url;
      });
    }
    onImagesChange(prev => {
      const next = [...prev, ...results];
      return mode === 'doc' ? next.map((p, i) => ({ ...p, pageNum: i + 1 })) : next;
    });

    // Usage: this is best-effort (backend requires auth). "Upload" alone is not DONE.
    // Start a new session only when the user uploads into an empty workspace (new run).
    if (results.length > 0) {
      const workflowSteps = usageWorkflowFromTabs(TABS.filter(t => t.modes.includes(mode)));
      if (startingFresh) startUsageSession('image_insights');
      usageStep('image_insights', 'upload', { count: results.length, mode }, workflowSteps);
    }
    setLoading(false); setProgress(0);
  }, [images, mode, onImagesChange]);

  const removeImage = id => onImagesChange(prev => {
    const next = prev.filter(x => x.id !== id);
    return mode === 'doc' ? next.map((p, i) => ({ ...p, pageNum: i + 1 })) : next;
  });

  const movePage = (id, dir) => onImagesChange(prev => {
    const idx = prev.findIndex(p => p.id === id);
    if (idx + dir < 0 || idx + dir >= prev.length) return prev;
    const next = [...prev]; [next[idx], next[idx + dir]] = [next[idx + dir], next[idx]];
    return next.map((p, i) => ({ ...p, pageNum: i + 1 }));
  });

  const statusColor = s => s === 'scanned' ? C.green : s === 'enhanced' ? C.gold : C.t3;
  const statusIcon  = s => s === 'scanned' ? '✓ SCANNED' : s === 'enhanced' ? '⚡ ENHANCED' : '◎ READY';

  const isEmpty = images.length === 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Mode selector */}
      <div style={{ display: 'flex', gap: 10, padding: '10px 14px', background: C.surface, border: `1px solid ${C.b1}`, borderRadius: 4, alignItems: 'center' }}>
        <span style={{ fontSize: 9, color: C.t4, fontFamily: 'JetBrains Mono,monospace', letterSpacing: '.12em' }}>MODE</span>
        {[['vision', '🔬', 'VISION ANALYSIS', 'Pixel stats · palette · ML detection · clustering · augmentation', C.cyan],
          ['doc', '📄', 'DOCUMENT OCR', 'Enhance → scan → edit → extract entities + tables → report', C.violet]
        ].map(([v, ic, label, desc, color]) => (
          <div key={v} onClick={() => { if (images.length === 0) onModeChange(v); }}
            style={{ flex: 1, padding: '10px 14px', background: mode === v ? color + '10' : C.deep, border: `2px solid ${mode === v ? color : C.b1}`, borderRadius: 4, cursor: images.length === 0 ? 'pointer' : 'default', opacity: images.length > 0 && mode !== v ? 0.4 : 1, transition: 'all .15s' }}>
            <div style={{ fontSize: 11, color, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700, marginBottom: 3 }}>{ic} {label}</div>
            <div style={{ fontSize: 9, color: C.t3, lineHeight: 1.5 }}>{desc}</div>
          </div>
        ))}
        {images.length > 0 && <span style={{ fontSize: 9, color: C.t4, fontFamily: 'JetBrains Mono,monospace' }}>Clear images to switch mode</span>}
      </div>

      {/* Drop zone */}
      <div
        onDragOver={e => { e.preventDefault(); setIsDrag(true); }}
        onDragLeave={() => setIsDrag(false)}
        onDrop={e => { e.preventDefault(); setIsDrag(false); processFiles(e.dataTransfer.files); }}
        onClick={() => !loading && inputRef.current?.click()}
        style={{ border: `2px dashed ${isDrag ? (mode === 'doc' ? C.violet : C.cyan) : C.b2}`, borderRadius: 6, padding: '44px 20px', textAlign: 'center', cursor: loading ? 'wait' : 'pointer', background: isDrag ? (mode === 'doc' ? C.violet : C.cyan) + '08' : C.deep, transition: 'all .15s' }}>
        {loading
          ? <div>
              <div style={{ fontSize: 32, marginBottom: 10, opacity: 0.5 }}>⟳</div>
              <div style={{ fontFamily: 'Syne,sans-serif', fontWeight: 700, fontSize: 15, color: C.t1, marginBottom: 8 }}>Processing images… {progress}%</div>
              <div style={{ width: 200, margin: '0 auto' }}><MBar pct={progress} color={mode === 'doc' ? C.violet : C.cyan} h={6} /></div>
            </div>
          : <>
              <div style={{ fontSize: 48, marginBottom: 10, opacity: 0.35 }}>{mode === 'doc' ? '📄' : '🖼'}</div>
              <div style={{ fontFamily: 'Syne,sans-serif', fontWeight: 800, fontSize: 17, color: C.t1, marginBottom: 6 }}>
                {images.length > 0 ? `${images.length} ${mode === 'doc' ? 'page' : 'image'}${images.length !== 1 ? 's' : ''} loaded — drop more` : `Drop ${mode === 'doc' ? 'document images' : 'images'} here`}
              </div>
              <div style={{ fontSize: 11, color: C.t3, marginBottom: 14 }}>
                {mode === 'doc' ? `Up to ${MAX_DOC} pages · JPG, PNG, WebP, TIFF` : `Up to ${MAX_VISION.toLocaleString()} images · 1 image = deep analysis · batch = corpus intelligence`}
              </div>
              <div style={{ display: 'inline-flex', gap: 8 }}>
                {['.jpg', '.png', '.webp', '.tiff', '.bmp'].map(e => (
                  <span key={e} style={{ fontSize: 9, padding: '2px 8px', background: C.raised, border: `1px solid ${C.b1}`, borderRadius: 2, color: C.t4, fontFamily: 'JetBrains Mono,monospace' }}>{e}</span>
                ))}
              </div>
              <div style={{ marginTop: 14 }}>
                <span style={{ display:'inline-block', padding:'4px 10px', borderRadius:999, background:`linear-gradient(90deg, ${(mode === 'doc' ? C.violet : C.cyan)}18, ${C.green}12)`, border:`1px solid ${(mode === 'doc' ? C.violet : C.cyan)}33`, color:C.t1, fontSize:10 }}>
                  Your data never leaves your machine. 100% on Browser & 100% Privacy.
                </span>
              </div>
            </>}
        <input ref={inputRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={e => processFiles(e.target.files)} />
      </div>

      {/* Stats row */}
      {images.length > 0 && mode === 'vision' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 10 }}>
          <MB label="IMAGES"        value={images.length} color={C.cyan} />
          <MB label="TOTAL SIZE"    value={`${(images.reduce((s, i) => s + i.fileSize, 0) / 1024).toFixed(1)}MB`} color={C.violet} />
          <MB label="AVG BRIGHT"    value={`${(images.reduce((s, i) => s + i.metrics.brightness, 0) / images.length).toFixed(0)}%`} color={C.gold} />
          <MB label="AVG SHARPNESS" value={(images.reduce((s, i) => s + i.metrics.sharpness, 0) / images.length).toFixed(0)} color={C.green} sub="Laplacian var" />
          <MB label="FORMATS"       value={[...new Set(images.map(i => i.fileType.split('/')[1]))].join('·')} color={C.t2} />
        </div>
      )}
      {images.length > 0 && mode === 'doc' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10 }}>
          <MB label="PAGES"    value={images.length} color={C.violet} />
          <MB label="ENHANCED" value={images.filter(p => p.processedUrl).length} color={C.gold} sub="ready for OCR" />
          <MB label="SCANNED"  value={images.filter(p => p.status === 'scanned').length} color={C.green} sub="OCR complete" />
          <MB label="WORDS"    value={images.reduce((a, p) => a + (p.text || '').split(/\s+/).filter(Boolean).length, 0).toLocaleString()} color={C.cyan} />
        </div>
      )}

      {/* Vision: thumbnail grid */}
      {images.length > 0 && mode === 'vision' && (
        <Card>
          <Head label={`IMAGE COLLECTION — ${images.length} IMAGES`} accent={C.cyan}
            right={<button onClick={() => onImagesChange([])} style={{ padding: '3px 10px', background: 'transparent', border: `1px solid ${C.red}33`, color: C.red, fontSize: 8, borderRadius: 2, cursor: 'pointer', fontFamily: 'JetBrains Mono,monospace' }}>✕ CLEAR</button>} />
          <Body>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, maxHeight: 480, overflowY: 'auto' }}>
              {images.slice(0, 200).map(img => (
                <div key={img.id} style={{ position: 'relative' }}>
                  <ImgThumb img={img} size={80} label={img.name} badge={`${img.width}×${img.height}`} badgeColor={C.t4} />
                  <button onClick={() => removeImage(img.id)} style={{ position: 'absolute', top: 3, left: 3, background: 'rgba(248,113,113,.9)', border: 'none', color: '#fff', width: 14, height: 14, borderRadius: '50%', cursor: 'pointer', fontSize: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>×</button>
                </div>
              ))}
              {images.length > 200 && <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 80, height: 96, background: C.raised, borderRadius: 3, border: `1px solid ${C.b1}`, fontSize: 10, color: C.t3 }}>+{images.length - 200} more</div>}
            </div>
          </Body>
        </Card>
      )}

      {/* Doc: page cards */}
      {images.length > 0 && mode === 'doc' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 9, color: C.t3, fontFamily: 'JetBrains Mono,monospace', letterSpacing: '.12em' }}>{images.length} PAGE{images.length !== 1 ? 'S' : ''} — USE ARROWS TO REORDER</div>
            <Btn onClick={() => onImagesChange([])} color={C.red} small>✕ CLEAR ALL</Btn>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))', gap: 12 }}>
            {images.map(page => (
              <Card key={page.id} style={{ borderTop: `2px solid ${statusColor(page.status)}` }}>
                <div style={{ position: 'relative', background: C.deep, height: 130, overflow: 'hidden' }}>
                  <img src={page.processedUrl || page.url} alt={page.name} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  <div style={{ position: 'absolute', top: 6, left: 6, background: 'rgba(6,11,20,.9)', borderRadius: 2, padding: '2px 7px', fontSize: 9, color: C.violet, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700 }}>P{page.pageNum}</div>
                  <div style={{ position: 'absolute', top: 6, right: 6, background: 'rgba(6,11,20,.9)', borderRadius: 2, padding: '2px 7px', fontSize: 8, color: statusColor(page.status), fontFamily: 'JetBrains Mono,monospace', fontWeight: 700 }}>{statusIcon(page.status)}</div>
                </div>
                <Body style={{ padding: '10px 12px' }}>
                  <div style={{ fontSize: 11, color: C.t1, fontWeight: 600, marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{page.name}</div>
                  <div style={{ display: 'flex', gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 8, color: C.t4, fontFamily: 'JetBrains Mono,monospace' }}>{page.width}×{page.height}</span>
                    <span style={{ fontSize: 8, color: C.t4, fontFamily: 'JetBrains Mono,monospace' }}>{page.fileSize}KB</span>
                    {page.confidence != null && <ConfBadge conf={page.confidence} />}
                  </div>
                  {page.text && <div style={{ fontSize: 9, color: C.t3, lineHeight: 1.5, maxHeight: 36, overflow: 'hidden' }}>{page.text.slice(0, 70)}…</div>}
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button onClick={() => movePage(page.id, -1)} disabled={page.pageNum === 1} style={{ background: 'none', border: `1px solid ${C.b1}`, color: C.t3, cursor: 'pointer', borderRadius: 2, padding: '2px 7px', fontSize: 11 }}>←</button>
                      <button onClick={() => movePage(page.id, 1)} disabled={page.pageNum === images.length} style={{ background: 'none', border: `1px solid ${C.b1}`, color: C.t3, cursor: 'pointer', borderRadius: 2, padding: '2px 7px', fontSize: 11 }}>→</button>
                    </div>
                    <button onClick={() => removeImage(page.id)} style={{ background: 'none', border: `1px solid ${C.red}33`, color: C.red, cursor: 'pointer', borderRadius: 2, padding: '2px 8px', fontSize: 9, fontFamily: 'JetBrains Mono,monospace' }}>✕</button>
                  </div>
                </Body>
              </Card>
            ))}
          </div>
        </>
      )}

      {/* Empty state explainer */}
      {isEmpty && (
        <div style={{ display: 'grid', gridTemplateColumns: mode === 'vision' ? '1fr 1fr' : 'repeat(3,1fr)', gap: 12 }}>
          {mode === 'vision' ? [
            ['🔬', 'SINGLE IMAGE', 'Deep pixel analysis — histogram, channel stats, entropy, sharpness, brightness, palette, ML detection', C.cyan],
            ['📊', 'BATCH MODE', 'Up to 10,000 images — corpus stats, visual search, k-means clustering, PCA scatter, augmentation pipeline', C.violet],
          ].map(([ic, t, d, color]) => (
            <div key={t} style={{ padding: 14, background: C.raised, border: `1px solid ${color}33`, borderLeft: `3px solid ${color}`, borderRadius: 4 }}>
              <div style={{ fontSize: 24, marginBottom: 8, opacity: 0.5 }}>{ic}</div>
              <div style={{ fontSize: 11, color, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700, marginBottom: 4 }}>{t}</div>
              <div style={{ fontSize: 10, color: C.t3, lineHeight: 1.6 }}>{d}</div>
            </div>
          )) : [
            ['📄', 'Invoices & Receipts', 'Extracts amounts, dates, vendor info automatically'],
            ['📝', 'Handwritten Notes', 'Converts handwriting to searchable text with Tesseract'],
            ['📋', 'Forms & Tables', 'Detects tabular structure and preserves layout'],
            ['🖼', 'Screenshots', 'OCRs any text visible in screen captures'],
            ['📃', 'Contracts & Reports', 'Multi-page document intelligence + entity extraction'],
            ['🏷', 'Labels & Packaging', 'Reads product info, barcodes, and compliance text'],
          ].map(([ic, title, desc]) => (
            <div key={title} style={{ padding: 14, background: C.raised, border: `1px solid ${C.b1}`, borderRadius: 4 }}>
              <div style={{ fontSize: 24, marginBottom: 8, opacity: 0.5 }}>{ic}</div>
              <div style={{ fontSize: 11, color: C.t1, fontWeight: 600, marginBottom: 4 }}>{title}</div>
              <div style={{ fontSize: 10, color: C.t3, lineHeight: 1.6 }}>{desc}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// ── 05  INSPECT TAB ──────────────────────────────────────────────────────────
// ═════════════════════════════════════════════════════════════════════════════

function InspectTab({ images }) {
  const [selIdx, setSelIdx] = useState(0);
  const [view, setView] = useState('single');
  const [stats, setStats] = useState(null);
  const [hist, setHist]   = useState(null);
  const [loading, setLoading] = useState(false);

  const img = images[selIdx];
  const isBatch = images.length > 1;

  useEffect(() => {
    if (!img) return;
    setStats(null); setHist(null); setLoading(true);
    setTimeout(() => {
      const el = new Image(); el.src = img.url;
      const run = () => { setStats(channelStats(el)); setHist(rgbHistogram(el)); setLoading(false); };
      el.onload = run;
      if (el.complete) run();
    }, 0);
  }, [img?.id]);

  const corpusStats = useMemo(() => {
    if (!isBatch || images.length === 0) return null;
    const bs = images.map(i => i.metrics?.brightness || 0);
    const sh = images.map(i => i.metrics?.sharpness || 0);
    const ws = images.map(i => i.width), hs = images.map(i => i.height);
    const fs = images.map(i => i.fileSize);
    const ar = images.map(i => +(i.width / i.height).toFixed(2));
    const hist_b = Array.from({ length: 20 }, (_, i) => ({ bin: `${i * 5}–${i * 5 + 5}`, count: bs.filter(v => v >= i * 5 && v < i * 5 + 5).length }));
    const hist_s = Array.from({ length: 20 }, (_, i) => ({ bin: `${i * 50}`, count: sh.filter(v => v >= i * 50 && v < i * 50 + 50).length }));
    const aspectBuckets = { 'portrait <0.8': 0, 'square 0.8–1.2': 0, 'landscape >1.2': 0 };
    ar.forEach(a => { if (a < 0.8) aspectBuckets['portrait <0.8']++; else if (a <= 1.2) aspectBuckets['square 0.8–1.2']++; else aspectBuckets['landscape >1.2']++; });
    return {
      hist_b, hist_s, aspectBuckets,
      avg: {
        brightness: +(bs.reduce((s, v) => s + v, 0) / bs.length).toFixed(1),
        sharpness: +(sh.reduce((s, v) => s + v, 0) / sh.length).toFixed(0),
        w: +(ws.reduce((s, v) => s + v, 0) / ws.length).toFixed(0),
        h: +(hs.reduce((s, v) => s + v, 0) / hs.length).toFixed(0),
        size: +(fs.reduce((s, v) => s + v, 0) / fs.length).toFixed(1),
      },
    };
  }, [images, isBatch]);

  if (!img) return <div style={{ padding: 60, textAlign: 'center', color: C.t4 }}>No images loaded — go to UPLOAD first.</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {isBatch && (
        <div style={{ display: 'flex', gap: 4 }}>
          {[['single', 'SINGLE IMAGE'], ['corpus', 'CORPUS STATS']].map(([v, l]) => (
            <button key={v} onClick={() => setView(v)} style={{ padding: '6px 14px', border: `1px solid ${view === v ? C.cyan : C.b0}`, background: view === v ? C.cyan + '15' : C.raised, color: view === v ? C.cyan : C.t2, fontSize: 9, cursor: 'pointer', borderRadius: 3, fontFamily: 'JetBrains Mono,monospace', fontWeight: view === v ? 700 : 400 }}>{l}</button>
          ))}
        </div>
      )}

      {view === 'single' && (
        <>
          {isBatch && <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4 }}>{images.map((m, i) => <ImgThumb key={m.id} img={m} selected={i === selIdx} onClick={() => setSelIdx(i)} size={60} label={m.name} />)}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Card>
              <Head label={img.name} accent={C.cyan} sub={`${img.width}×${img.height} · ${img.fileSize}KB · ${img.fileType}`} />
              <Body>
                <div style={{ background: C.deep, borderRadius: 3, overflow: 'hidden', marginBottom: 14 }}>
                  <img src={img.url} alt={img.name} style={{ width: '100%', maxHeight: 300, objectFit: 'contain', display: 'block' }} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <MB label="BRIGHTNESS"  value={`${img.metrics?.brightness || 0}%`} color={C.gold} sub="perceptual luma" />
                  <MB label="CONTRAST"    value={`${img.metrics?.contrast || 0}%`}   color={C.violet} sub="RMS contrast" />
                  <MB label="SHARPNESS"   value={img.metrics?.sharpness || 0}        color={C.cyan}   sub="Laplacian var" />
                  <MB label="MEGAPIXELS"  value={+(img.width * img.height / 1e6).toFixed(2)} color={C.green} sub="MP" />
                </div>
              </Body>
            </Card>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {loading && <div style={{ padding: 40, textAlign: 'center', color: C.t4, fontFamily: 'JetBrains Mono,monospace', fontSize: 10 }}>Computing channel statistics…</div>}
              {stats && Object.entries(stats).filter(([k]) => k !== 'pixels').map(([ch, s]) => {
                const color = { R: C.red, G: C.green, B: C.blue, L: C.t2 }[ch] || C.cyan;
                return (
                  <Card key={ch}>
                    <Head label={`${ch === 'L' ? 'LUMA' : ch} CHANNEL`} accent={color} />
                    <Body style={{ padding: 10 }}>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 6, marginBottom: 8 }}>
                        {[['MEAN', s.mean], ['STD', s.std], ['MIN', s.min], ['MAX', s.max], ['MED', s.median]].map(([l, v]) => (
                          <div key={l} style={{ textAlign: 'center', background: C.deep, borderRadius: 3, padding: '4px 2px' }}>
                            <div style={{ fontSize: 7, color: C.t4, fontFamily: 'JetBrains Mono,monospace' }}>{l}</div>
                            <div style={{ fontSize: 11, color, fontWeight: 700, fontFamily: 'JetBrains Mono,monospace' }}>{v}</div>
                          </div>
                        ))}
                      </div>
                      <div style={{ height: 6, background: C.b1, borderRadius: 3, overflow: 'hidden', position: 'relative' }}>
                        <div style={{ position: 'absolute', left: `${s.p25 / 255 * 100}%`, right: `${(255 - s.p75) / 255 * 100}%`, top: 0, bottom: 0, background: color, opacity: 0.5, borderRadius: 2 }} />
                        <div style={{ position: 'absolute', left: `${s.mean / 255 * 100}%`, top: 0, bottom: 0, width: 2, background: color }} />
                      </div>
                    </Body>
                  </Card>
                );
              })}
            </div>
          </div>
          {hist && (
            <Card>
              <Head label="RGB HISTOGRAM — pixel value distribution" accent={C.cyan} />
              <Body>
                <ResponsiveContainer width="100%" height={180}>
                  <AreaChart data={hist} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="2 2" stroke={C.b0} />
                    <XAxis dataKey="bin" tick={{ fill: C.t4, fontSize: 8 }} />
                    <YAxis tick={{ fill: C.t4, fontSize: 8 }} />
                    <Tooltip content={<ChartTip />} />
                    <Area type="monotone" dataKey="R" stroke={C.red}   fill={C.red}   fillOpacity={0.25} strokeWidth={1.5} />
                    <Area type="monotone" dataKey="G" stroke={C.green} fill={C.green} fillOpacity={0.25} strokeWidth={1.5} />
                    <Area type="monotone" dataKey="B" stroke={C.blue}  fill={C.blue}  fillOpacity={0.25} strokeWidth={1.5} />
                  </AreaChart>
                </ResponsiveContainer>
              </Body>
            </Card>
          )}
        </>
      )}

      {view === 'corpus' && corpusStats && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 10 }}>
            <MB label="IMAGES"      value={images.length}                    color={C.cyan} />
            <MB label="AVG WIDTH"   value={`${corpusStats.avg.w}px`}         color={C.violet} />
            <MB label="AVG HEIGHT"  value={`${corpusStats.avg.h}px`}         color={C.violet} />
            <MB label="AVG BRIGHT"  value={`${corpusStats.avg.brightness}%`} color={C.gold} />
            <MB label="AVG SIZE"    value={`${corpusStats.avg.size}KB`}       color={C.green} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            {[
              ['BRIGHTNESS DISTRIBUTION', C.gold, corpusStats.hist_b, 'count'],
              ['SHARPNESS DISTRIBUTION',  C.cyan, corpusStats.hist_s, 'count'],
            ].map(([label, color, data, key]) => (
              <Card key={label}>
                <Head label={label} accent={color} />
                <Body>
                  <ResponsiveContainer width="100%" height={160}>
                    <BarChart data={data} barCategoryGap="2%">
                      <CartesianGrid strokeDasharray="2 2" stroke={C.b0} />
                      <XAxis dataKey="bin" tick={{ fill: C.t4, fontSize: 7 }} />
                      <YAxis tick={{ fill: C.t4, fontSize: 8 }} />
                      <Tooltip content={<ChartTip />} />
                      <Bar dataKey={key} fill={color} opacity={0.8} radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Body>
              </Card>
            ))}
            <Card>
              <Head label="ASPECT RATIO DISTRIBUTION" accent={C.violet} />
              <Body>
                <ResponsiveContainer width="100%" height={160}>
                  <BarChart data={Object.entries(corpusStats.aspectBuckets).map(([n, c]) => ({ name: n, count: c }))} barCategoryGap="20%">
                    <CartesianGrid strokeDasharray="2 2" stroke={C.b0} />
                    <XAxis dataKey="name" tick={{ fill: C.t3, fontSize: 9 }} />
                    <YAxis tick={{ fill: C.t4, fontSize: 8 }} />
                    <Tooltip content={<ChartTip />} />
                    <Bar dataKey="count" radius={[3, 3, 0, 0]}>{Object.keys(corpusStats.aspectBuckets).map((_, i) => <Cell key={i} fill={PALETTE[i]} />)}</Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Body>
            </Card>
            <Card>
              <Head label="BRIGHTNESS vs SHARPNESS scatter" accent={C.violet} sub="each dot = one image" />
              <Body>
                <ResponsiveContainer width="100%" height={160}>
                  <ScatterChart>
                    <CartesianGrid strokeDasharray="2 2" stroke={C.b0} />
                    <XAxis dataKey="x" name="Brightness" tick={{ fill: C.t4, fontSize: 9 }} />
                    <YAxis dataKey="y" name="Sharpness" tick={{ fill: C.t4, fontSize: 8 }} />
                    <Tooltip content={<ChartTip />} />
                    <Scatter data={images.slice(0, 500).map(i => ({ x: i.metrics?.brightness || 0, y: Math.min(i.metrics?.sharpness || 0, 2000), name: i.name }))} fill={C.violet} opacity={0.7} />
                  </ScatterChart>
                </ResponsiveContainer>
              </Body>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// ── 06  PALETTE TAB ──────────────────────────────────────────────────────────
// ═════════════════════════════════════════════════════════════════════════════

function PaletteTab({ images }) {
  const [selIdx, setSelIdx] = useState(0);
  const [k, setK] = useState(8);
  const [palette, setPalette]   = useState(null);
  const [loading, setLoading]   = useState(false);
  const [view, setView]         = useState('single');
  const [corpusPalettes, setCorpusPalettes] = useState(null);
  const [corpusLoading, setCorpusLoading]   = useState(false);

  const img = images[selIdx];
  const isBatch = images.length > 1;

  const extractSingle = useCallback(async () => {
    if (!img) return;
    setLoading(true);
    await new Promise(r => setTimeout(r, 10));
    const el = new Image(); el.src = img.url;
    await new Promise(r => { el.onload = r; if (el.complete) r(); });
    setPalette(dominantPalette(el, k));
    setLoading(false);
  }, [img, k]);

  const extractCorpus = useCallback(async () => {
    setCorpusLoading(true);
    const palettes = [];
    for (let i = 0; i < images.length; i++) {
      await new Promise(r => setTimeout(r, 0));
      const el = new Image(); el.src = images[i].url;
      await new Promise(r => { el.onload = r; if (el.complete) r(); });
      palettes.push({ id: images[i].id, name: images[i].name, url: images[i].url, palette: dominantPalette(el, 5) });
    }
    setCorpusPalettes(palettes);
    setCorpusLoading(false);
  }, [images]);

  const globalColors = useMemo(() => {
    if (!corpusPalettes) return [];
    const hueMap = {};
    corpusPalettes.forEach(({ palette }) => palette.forEach(c => {
      const bucket = Math.round(c.hsl.h / 30) * 30;
      if (!hueMap[bucket]) hueMap[bucket] = { hue: bucket, count: 0 };
      hueMap[bucket].count++;
    }));
    return Object.values(hueMap).sort((a, b) => b.count - a.count).slice(0, 12);
  }, [corpusPalettes]);

  const harmony = useMemo(() => detectHarmony(palette), [palette]);

  if (!img) return <div style={{ padding: 60, textAlign: 'center', color: C.t4 }}>No images loaded.</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {isBatch && (
        <div style={{ display: 'flex', gap: 4 }}>
          {[['single', 'SINGLE IMAGE'], ['corpus', 'CORPUS PALETTE']].map(([v, l]) => (
            <button key={v} onClick={() => setView(v)} style={{ padding: '6px 14px', border: `1px solid ${view === v ? C.gold : C.b0}`, background: view === v ? C.gold + '15' : C.raised, color: view === v ? C.gold : C.t2, fontSize: 9, cursor: 'pointer', borderRadius: 3, fontFamily: 'JetBrains Mono,monospace', fontWeight: view === v ? 700 : 400 }}>{l}</button>
          ))}
        </div>
      )}

      {view === 'single' && (
        <>
          {isBatch && <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4 }}>{images.map((m, i) => <ImgThumb key={m.id} img={m} selected={i === selIdx} onClick={() => { setSelIdx(i); setPalette(null); }} size={60} />)}</div>}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 9, color: C.t3, fontFamily: 'JetBrains Mono,monospace' }}>COLORS (k):</span>
            {[4, 6, 8, 10, 12, 16].map(n => (
              <button key={n} onClick={() => setK(n)} style={{ padding: '4px 10px', border: `1px solid ${k === n ? C.gold : C.b1}`, background: k === n ? C.gold + '18' : 'transparent', color: k === n ? C.gold : C.t3, fontSize: 9, borderRadius: 3, cursor: 'pointer', fontFamily: 'JetBrains Mono,monospace', fontWeight: 700 }}>{n}</button>
            ))}
            <Btn onClick={extractSingle} color={C.gold} disabled={loading}>{loading ? '⟳ EXTRACTING…' : '▶ EXTRACT PALETTE'}</Btn>
          </div>

          {palette && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <Card>
                <Head label={img.name} accent={C.gold} />
                <Body>
                  <img src={img.url} alt="" style={{ width: '100%', maxHeight: 260, objectFit: 'contain', borderRadius: 3, display: 'block', marginBottom: 12 }} />
                  <div style={{ display: 'flex', height: 40, borderRadius: 4, overflow: 'hidden', marginBottom: 12 }}>
                    {palette.map((c, i) => <div key={i} style={{ flex: c.pct, background: c.hex }} title={`${c.hex} · ${c.pct}%`} />)}
                  </div>
                  {harmony && (
                    <div style={{ padding: '8px 12px', background: harmony.color + '12', border: `1px solid ${harmony.color}44`, borderRadius: 3 }}>
                      <span style={{ fontSize: 9, color: harmony.color, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700 }}>{harmony.name}</span>
                      <span style={{ fontSize: 10, color: C.t2, marginLeft: 8 }}>{harmony.desc}</span>
                    </div>
                  )}
                </Body>
              </Card>
              <Card>
                <Head label={`${k} DOMINANT COLORS`} accent={C.gold} />
                <Body>
                  {palette.map((c, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: `1px solid ${C.b0}` }}>
                      <div style={{ width: 36, height: 36, borderRadius: 4, background: c.hex, flexShrink: 0, boxShadow: '0 2px 8px rgba(0,0,0,.4)' }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 11, color: C.t1, fontWeight: 700 }}>{c.hex.toUpperCase()}</div>
                        <div style={{ fontSize: 9, color: C.t3 }}>RGB({c.r},{c.g},{c.b}) · HSL({c.hsl.h}°,{c.hsl.s}%,{c.hsl.l}%)</div>
                        <MBar pct={c.pct} color={c.hex} h={3} />
                      </div>
                      <div style={{ fontFamily: 'Syne,sans-serif', fontWeight: 800, fontSize: 14, color: C.t1, minWidth: 36, textAlign: 'right' }}>{c.pct}%</div>
                    </div>
                  ))}
                </Body>
              </Card>
            </div>
          )}
          {!palette && <Info color={C.gold}>Click EXTRACT PALETTE to run k-means color clustering. Adjust k for more or fewer dominant colors.</Info>}
        </>
      )}

      {view === 'corpus' && (
        <>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Btn onClick={extractCorpus} color={C.gold} disabled={corpusLoading}>{corpusLoading ? '⟳ EXTRACTING ALL…' : '▶ EXTRACT ALL PALETTES'}</Btn>
            {corpusPalettes && <span style={{ fontSize: 10, color: C.green }}>✓ {corpusPalettes.length} palettes extracted</span>}
          </div>
          {corpusPalettes && (
            <>
              <Card>
                <Head label="GLOBAL HUE FREQUENCY — most common colors across all images" accent={C.gold} />
                <Body>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                    {globalColors.map((h, i) => (
                      <div key={i} style={{ textAlign: 'center' }}>
                        <div style={{ width: 32, height: 32, borderRadius: '50%', background: `hsl(${h.hue},70%,55%)`, marginBottom: 4, boxShadow: '0 2px 8px rgba(0,0,0,.4)' }} />
                        <div style={{ fontSize: 7, color: C.t4, fontFamily: 'JetBrains Mono,monospace' }}>{h.hue}°</div>
                        <div style={{ fontSize: 8, color: C.t2, fontWeight: 700 }}>{h.count}</div>
                      </div>
                    ))}
                  </div>
                  <ResponsiveContainer width="100%" height={100}>
                    <BarChart data={globalColors} barCategoryGap="10%">
                      <CartesianGrid strokeDasharray="2 2" stroke={C.b0} />
                      <XAxis dataKey="hue" tick={{ fill: C.t4, fontSize: 8 }} />
                      <YAxis tick={{ fill: C.t4, fontSize: 8 }} />
                      <Tooltip content={<ChartTip />} />
                      <Bar dataKey="count" radius={[2, 2, 0, 0]}>{globalColors.map((h, i) => <Cell key={i} fill={`hsl(${h.hue},70%,55%)`} />)}</Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Body>
              </Card>
              <Card>
                <Head label="PER-IMAGE PALETTE STRIPS" accent={C.gold} />
                <Body style={{ maxHeight: 500, overflowY: 'auto' }}>
                  {corpusPalettes.slice(0, 100).map(({ id, name, palette }) => (
                    <div key={id} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, padding: '6px 0', borderBottom: `1px solid ${C.b0}` }}>
                      <div style={{ fontSize: 9, color: C.t3, fontFamily: 'JetBrains Mono,monospace', minWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
                      <div style={{ flex: 1, display: 'flex', height: 20, borderRadius: 2, overflow: 'hidden' }}>
                        {palette.map((c, i) => <div key={i} style={{ flex: c.pct, background: c.hex }} title={c.hex} />)}
                      </div>
                    </div>
                  ))}
                </Body>
              </Card>
            </>
          )}
        </>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// ── 07  ENHANCE TAB — unified for both modes ─────────────────────────────────
// ═════════════════════════════════════════════════════════════════════════════

const DEFAULT_FILTERS = { brightness: 1, contrast: 1, saturation: 1, hue: 0, temperature: 0, grayscale: false, invert: false, threshold: 0, sepia: false, sharpen: false, blur: false, edges: false };

const ENHANCE_PRESETS = [
  { name: 'VIVID',      f: { brightness: 1.1, contrast: 1.3, saturation: 1.4, temperature: .2 },           desc: 'Punchy, saturated' },
  { name: 'MATTE',      f: { brightness: 1.05, contrast: .85, saturation: .7 },                             desc: 'Flat, editorial' },
  { name: 'NOIR',       f: { grayscale: true, contrast: 1.5, brightness: .9 },                              desc: 'High-contrast B&W' },
  { name: 'WARM',       f: { temperature: .5, saturation: 1.1, brightness: 1.05 },                          desc: 'Golden hour' },
  { name: 'COOL',       f: { temperature: -.5, saturation: 1.1, brightness: 1.05 },                         desc: 'Blue shift' },
  { name: 'DOCUMENT',   f: { brightness: 1.1, contrast: 1.3, threshold: 0, sharpen: true, grayscale: true },desc: 'Best for printed text' },
  { name: 'RECEIPT',    f: { brightness: 1.2, contrast: 1.5, threshold: 130, sharpen: true, grayscale: true },desc: 'Thermal receipt' },
  { name: 'WHITEBOARD', f: { brightness: .9, contrast: 1.4, sharpen: true, grayscale: true },               desc: 'Low-contrast board' },
  { name: 'EDGE MAP',   f: { edges: true },                                                                  desc: 'Sobel edge detection' },
  { name: 'SEPIA',      f: { sepia: true },                                                                  desc: 'Antique warm tone' },
  { name: 'SHARPEN',    f: { sharpen: true, contrast: 1.1 },                                                 desc: 'Crisp edges' },
];

function EnhanceTab({ images, onImagesChange, mode }) {
  const [selIdx, setSelIdx] = useState(0);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [result, setResult]   = useState(null);
  const [applying, setApplying] = useState(false);
  const [batchProgress, setBatchProgress] = useState(0);
  const [preset, setPreset]   = useState(null);
  const imgRef = useRef();

  const img = images[selIdx];

  const applyOne = useCallback(async () => {
    if (!img) return;
    setApplying(true);
    await new Promise(r => setTimeout(r, 10));
    const el = new Image(); el.src = img.url;
    await new Promise(r => { el.onload = r; if (el.complete) r(); });
    const url = applyCanvasFilter(el, filters);
    setResult(url);
    onImagesChange(prev => prev.map((x, i) => i === selIdx ? { ...x, processedUrl: url, status: x.status !== 'scanned' ? 'enhanced' : x.status } : x));
    setApplying(false);
  }, [img, filters, selIdx, onImagesChange]);

  const applyAll = useCallback(async () => {
    setApplying(true);
    for (let i = 0; i < images.length; i++) {
      await new Promise(r => setTimeout(r, 0));
      setBatchProgress(Math.round(i / images.length * 100));
      const el = new Image(); el.src = images[i].url;
      await new Promise(r => { el.onload = r; if (el.complete) r(); });
      const url = applyCanvasFilter(el, filters);
      onImagesChange(prev => prev.map((x, ii) => ii === i ? { ...x, processedUrl: url, status: x.status !== 'scanned' ? 'enhanced' : x.status } : x));
    }
    setApplying(false); setBatchProgress(0);
  }, [images, filters, onImagesChange]);

  const setF = (key, val) => setFilters(f => ({ ...f, [key]: val }));

  if (!img) return <div style={{ padding: 60, textAlign: 'center', color: C.t4 }}>No images loaded.</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {images.length > 1 && (
        <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4 }}>
          {images.map((m, i) => mode === 'doc'
            ? <PageThumb key={m.id} img={m} selected={i === selIdx} onClick={() => { setSelIdx(i); setResult(null); }} size={64} />
            : <ImgThumb  key={m.id} img={m} selected={i === selIdx} onClick={() => { setSelIdx(i); setResult(null); }} size={64} />)}
        </div>
      )}

      {/* Presets */}
      <Card>
        <Head label="PRESETS" accent={C.orange} />
        <Body>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {ENHANCE_PRESETS.map(p => (
              <button key={p.name} title={p.desc} onClick={() => { setFilters({ ...DEFAULT_FILTERS, ...p.f }); setPreset(p.name); setResult(null); }}
                style={{ padding: '5px 11px', background: preset === p.name ? C.orange + '18' : C.deep, border: `1px solid ${preset === p.name ? C.orange : C.b2}`, color: preset === p.name ? C.orange : C.t2, fontSize: 9, borderRadius: 3, cursor: 'pointer', fontFamily: 'JetBrains Mono,monospace', fontWeight: 700 }}>{p.name}</button>
            ))}
            <button onClick={() => { setFilters(DEFAULT_FILTERS); setPreset(null); setResult(null); }}
              style={{ padding: '5px 11px', background: 'transparent', border: `1px solid ${C.b1}`, color: C.t3, fontSize: 9, borderRadius: 3, cursor: 'pointer', fontFamily: 'JetBrains Mono,monospace' }}>RESET</button>
          </div>
        </Body>
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: 14 }}>
        {/* Controls */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Card>
            <Head label="ADJUST" accent={C.orange} />
            <Body>
              {[
                ['BRIGHTNESS',  'brightness', .3, 2.5, .05, v => `${(v * 100).toFixed(0)}%`],
                ['CONTRAST',    'contrast',   .3, 3.0, .05, v => `${(v * 100).toFixed(0)}%`],
                ['SATURATION',  'saturation',  0, 3.0, .05, v => `${(v * 100).toFixed(0)}%`],
                ['HUE ROTATE',  'hue',       -180, 180, 1,  v => `${v}°`],
                ['TEMPERATURE', 'temperature',-1, 1,  .05, v => v > 0 ? `+${(v * 100).toFixed(0)}` : `${(v * 100).toFixed(0)}`],
                ['THRESHOLD',   'threshold',   0, 255, 1,   v => v > 0 ? `${v}` : 'OFF'],
              ].map(([l, k, min, max, step, fmt]) => (
                <div key={k} style={{ marginBottom: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                    <span style={{ fontSize: 8, color: C.t3, fontFamily: 'JetBrains Mono,monospace' }}>{l}</span>
                    <span style={{ fontSize: 9, color: C.orange, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700 }}>{fmt(filters[k])}</span>
                  </div>
                  <input type="range" min={min} max={max} step={step} value={filters[k]} onChange={e => setF(k, +e.target.value)} style={{ width: '100%', accentColor: C.orange, height: 3 }} />
                </div>
              ))}
            </Body>
          </Card>
          <Card>
            <Head label="EFFECTS" accent={C.orange} />
            <Body>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {[['grayscale', '◑ GRAYSCALE'], ['invert', '⊘ INVERT'], ['sepia', '◫ SEPIA'], ['sharpen', '⚡ SHARPEN'], ['blur', '◎ BLUR'], ['edges', '△ EDGE MAP']].map(([k, l]) => (
                  <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', padding: '6px 8px', background: filters[k] ? C.orange + '18' : C.deep, border: `1px solid ${filters[k] ? C.orange : C.b2}`, borderRadius: 3, transition: 'all .1s' }}>
                    <input type="checkbox" checked={!!filters[k]} onChange={e => setF(k, e.target.checked)} style={{ accentColor: C.orange, width: 12, height: 12 }} />
                    <span style={{ fontSize: 9, color: filters[k] ? C.orange : C.t3, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700 }}>{l}</span>
                  </label>
                ))}
              </div>
            </Body>
          </Card>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Btn onClick={applyOne} color={C.orange} disabled={applying}>{applying ? '⟳ APPLYING…' : '▶ APPLY'}</Btn>
            {result && <Btn onClick={() => { const a = document.createElement('a'); a.href = result; a.download = `${img.name}_enhanced.png`; a.click(); }} color={C.green} small>↓ SAVE</Btn>}
            {images.length > 1 && <Btn onClick={applyAll} color={C.violet} disabled={applying} small>ALL {images.length}</Btn>}
          </div>
          {applying && images.length > 1 && batchProgress > 0 && (
            <div><MBar pct={batchProgress} color={C.violet} h={5} /><div style={{ fontSize: 9, color: C.violet, marginTop: 4, fontFamily: 'JetBrains Mono,monospace' }}>Processing {batchProgress}%…</div></div>
          )}
        </div>

        {/* Before / After */}
        <Card>
          <Head label="BEFORE / AFTER" accent={C.orange} />
          <Body style={{ padding: 10 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {[['ORIGINAL', img.url, C.t4], ['PROCESSED', result || img.processedUrl, C.orange]].map(([label, src, color]) => (
                <div key={label}>
                  <div style={{ fontSize: 8, color, fontFamily: 'JetBrains Mono,monospace', textAlign: 'center', marginBottom: 6 }}>{label}</div>
                  <div style={{ background: C.deep, borderRadius: 3, overflow: 'hidden', border: `1px solid ${src ? color + '44' : C.b1}` }}>
                    {src
                      ? <img src={src} alt={label} style={{ width: '100%', maxHeight: 400, objectFit: 'contain', display: 'block' }} />
                      : <div style={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 8, color: C.t4, fontSize: 10 }}><div style={{ opacity: .2, fontSize: 32 }}>🔧</div>Click APPLY to preview</div>}
                  </div>
                </div>
              ))}
            </div>
          </Body>
        </Card>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// ── 08  TRANSFORM + AUGMENT TAB (vision only) ────────────────────────────────
// ═════════════════════════════════════════════════════════════════════════════

function TransformTab({ images, onImagesChange }) {
  const [selIdx, setSelIdx]   = useState(0);
  const [view, setView]       = useState('transform'); // transform | augment
  // Augment ops
  const [ops, setOps] = useState({ flipH: true, flipV: false, rotate90: true, rotate180: false, rotate270: false, noise: false, noiseAmt: 20, crop: false, cropPct: 80, brightness: false, brightAmt: 1.3, contrast: false, contrastAmt: 1.3, blur: false, grayscale: false });
  const [previews, setPreviews] = useState([]);
  const [generating, setGenerating] = useState(false);
  const setOp = (k, v) => setOps(o => ({ ...o, [k]: v }));

  const img = images[selIdx];

  const generatePreviews = useCallback(async () => {
    if (!img) return;
    setGenerating(true);
    const el = new Image(); el.src = img.url;
    await new Promise(r => { el.onload = r; if (el.complete) r(); });
    const W = el.naturalWidth, H = el.naturalHeight;
    const results = [{ name: 'original', url: img.url }];

    const mk = (transform) => { const c = document.createElement('canvas'); c.width = W; c.height = H; const ctx = c.getContext('2d'); transform(ctx, W, H, el); return c.toDataURL('image/jpeg', .9); };

    if (ops.flipH)    results.push({ name: 'flip_h',   url: mk((ctx,W,H,el)=>{ ctx.translate(W,0);ctx.scale(-1,1);ctx.drawImage(el,0,0,W,H); }) });
    if (ops.flipV)    results.push({ name: 'flip_v',   url: mk((ctx,W,H,el)=>{ ctx.translate(0,H);ctx.scale(1,-1);ctx.drawImage(el,0,0,W,H); }) });
    if (ops.rotate90) results.push({ name: 'rot_90',   url: mk((ctx,W,H,el)=>{ ctx.translate(H,0);ctx.rotate(Math.PI/2);ctx.drawImage(el,0,0,W,H); }) });
    if (ops.rotate180)results.push({ name: 'rot_180',  url: mk((ctx,W,H,el)=>{ ctx.translate(W,H);ctx.rotate(Math.PI);ctx.drawImage(el,0,0,W,H); }) });
    if (ops.rotate270)results.push({ name: 'rot_270',  url: mk((ctx,W,H,el)=>{ ctx.translate(0,W);ctx.rotate(-Math.PI/2);ctx.drawImage(el,0,0,W,H); }) });
    if (ops.brightness)results.push({ name: `bright_${ops.brightAmt}`, url: applyCanvasFilter(el, { brightness: ops.brightAmt }) });
    if (ops.contrast) results.push({ name: `contrast_${ops.contrastAmt}`, url: applyCanvasFilter(el, { contrast: ops.contrastAmt }) });
    if (ops.grayscale)results.push({ name: 'grayscale', url: applyCanvasFilter(el, { grayscale: true }) });
    if (ops.blur)     results.push({ name: 'blur',       url: applyCanvasFilter(el, { blur: true }) });
    if (ops.noise) {
      const c = document.createElement('canvas'); c.width = W; c.height = H;
      const ctx = c.getContext('2d'); ctx.drawImage(el, 0, 0);
      const id = ctx.getImageData(0, 0, W, H), d = id.data;
      for (let i = 0; i < d.length; i += 4) { const n = (Math.random() - .5) * ops.noiseAmt * 2; d[i] = Math.min(255, Math.max(0, d[i]+n)); d[i+1] = Math.min(255, Math.max(0, d[i+1]+n)); d[i+2] = Math.min(255, Math.max(0, d[i+2]+n)); }
      ctx.putImageData(id, 0, 0);
      results.push({ name: `noise_${ops.noiseAmt}`, url: c.toDataURL() });
    }
    if (ops.crop) {
      const pct = ops.cropPct / 100, cw = Math.floor(W*pct), ch = Math.floor(H*pct);
      [['tl',0,0],['tr',W-cw,0],['bl',0,H-ch],['br',W-cw,H-ch],['ctr',(W-cw)/2,(H-ch)/2]].forEach(([pos,sx,sy])=>{
        const c = document.createElement('canvas'); c.width = W; c.height = H;
        const ctx = c.getContext('2d'); ctx.drawImage(el, sx, sy, cw, ch, 0, 0, W, H);
        results.push({ name: `crop_${pos}_${ops.cropPct}pct`, url: c.toDataURL() });
      });
    }
    setPreviews(results); setGenerating(false);
  }, [img, ops]);

  const downloadAll = () => previews.forEach((p, i) => setTimeout(() => {
    const a = document.createElement('a'); a.href = p.url; a.download = `${img.name}_aug_${p.name}.jpg`; a.click();
  }, i * 120));

  if (!img) return <div style={{ padding: 60, textAlign: 'center', color: C.t4 }}>No images loaded.</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {images.length > 1 && <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4 }}>{images.map((m, i) => <ImgThumb key={m.id} img={m} selected={i === selIdx} onClick={() => { setSelIdx(i); setPreviews([]); }} size={64} />)}</div>}
      <div style={{ display: 'flex', gap: 4 }}>
        {[['transform', '⚡ TRANSFORM'], ['augment', '🔁 AUGMENT (ML TRAINING DATA)']].map(([v, l]) => (
          <button key={v} onClick={() => setView(v)} style={{ padding: '6px 14px', border: `1px solid ${view === v ? C.pink : C.b0}`, background: view === v ? C.pink + '15' : C.raised, color: view === v ? C.pink : C.t2, fontSize: 9, cursor: 'pointer', borderRadius: 3, fontFamily: 'JetBrains Mono,monospace', fontWeight: view === v ? 700 : 400 }}>{l}</button>
        ))}
      </div>

      {view === 'augment' && (
        <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 14 }}>
          <Card>
            <Head label="AUGMENTATION OPS" accent={C.pink} />
            <Body>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[
                  { section: 'GEOMETRIC' },
                  { k: 'flipH', l: '↔ Horizontal Flip' }, { k: 'flipV', l: '↕ Vertical Flip' },
                  { k: 'rotate90', l: '↻ Rotate 90°' }, { k: 'rotate180', l: '↻ Rotate 180°' }, { k: 'rotate270', l: '↻ Rotate 270°' },
                  { section: 'COLOR' },
                  { k: 'brightness', l: '☀ Brightness', sub: ops.brightness && { k: 'brightAmt', min: .5, max: 2, label: `${ops.brightAmt}x` } },
                  { k: 'contrast',   l: '◐ Contrast',   sub: ops.contrast   && { k: 'contrastAmt', min: .5, max: 2, label: `${ops.contrastAmt}x` } },
                  { k: 'grayscale', l: '◑ Grayscale' }, { k: 'blur', l: '◎ Blur' },
                  { section: 'ADVANCED' },
                  { k: 'noise', l: '〰 Gaussian Noise', sub: ops.noise && { k: 'noiseAmt', min: 5, max: 80, label: ops.noiseAmt } },
                  { k: 'crop',  l: '⬜ Random Crops (5 positions)', sub: ops.crop && { k: 'cropPct', min: 50, max: 95, label: `${ops.cropPct}%` } },
                ].map((item, idx) => {
                  if (item.section) return <div key={idx} style={{ fontSize: 8, color: C.t4, fontFamily: 'JetBrains Mono,monospace', letterSpacing: '.1em', marginTop: 4 }}>{item.section}</div>;
                  return (
                    <div key={item.k}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                        <input type="checkbox" checked={!!ops[item.k]} onChange={e => setOp(item.k, e.target.checked)} style={{ accentColor: C.pink, width: 13, height: 13 }} />
                        <span style={{ fontSize: 10, color: ops[item.k] ? C.pink : C.t2, fontFamily: 'JetBrains Mono,monospace' }}>{item.l}</span>
                      </label>
                      {item.sub && (
                        <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 6, paddingLeft: 20 }}>
                          <input type="range" min={item.sub.min} max={item.sub.max} value={ops[item.sub.k]} onChange={e => setOp(item.sub.k, +e.target.value)} style={{ flex: 1, accentColor: C.pink }} />
                          <span style={{ fontSize: 8, color: C.pink, fontFamily: 'JetBrains Mono,monospace', minWidth: 28 }}>{item.sub.label}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div style={{ marginTop: 14, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Btn onClick={generatePreviews} color={C.pink} disabled={generating}>{generating ? '⟳ GENERATING…' : '▶ GENERATE'}</Btn>
                {previews.length > 0 && <Btn onClick={downloadAll} color={C.green} small>↓ DOWNLOAD ALL ({previews.length})</Btn>}
              </div>
            </Body>
          </Card>
          <Card>
            <Head label="AUGMENTATION PREVIEWS" accent={C.pink} sub={previews.length > 0 ? `${previews.length} variants` : 'Configure ops and click GENERATE'} />
            <Body>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {previews.map(p => (
                  <div key={p.name} style={{ textAlign: 'center', width: 100 }}>
                    <div style={{ borderRadius: 3, overflow: 'hidden', border: `1px solid ${C.b1}`, marginBottom: 4 }}>
                      <img src={p.url} alt={p.name} style={{ width: '100%', height: 80, objectFit: 'cover', display: 'block' }} />
                    </div>
                    <div style={{ fontSize: 7, color: C.t4, fontFamily: 'JetBrains Mono,monospace', wordBreak: 'break-all' }}>{p.name}</div>
                  </div>
                ))}
                {previews.length === 0 && <div style={{ color: C.t4, fontSize: 11, padding: '40px 0' }}>No previews yet — select operations and click GENERATE.</div>}
              </div>
            </Body>
          </Card>
        </div>
      )}

      {view === 'transform' && (
        <Info color={C.orange}>Use the ENHANCE tab for full filter controls (brightness, contrast, saturation, hue, effects). Switch to AUGMENT mode above to generate ML training data variants.</Info>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// ── 09  DETECT TAB ───────────────────────────────────────────────────────────
// ═════════════════════════════════════════════════════════════════════════════

function DetectTab({ images, onImagesChange }) {
  const [selIdx, setSelIdx] = useState(0);
  const [model, setModel]   = useState('mobilenet');
  const [status, setStatus] = useState('idle');
  const [statusMsg, setStatusMsg] = useState('');
  const [results, setResults] = useState({});
  const [batchDone, setBatchDone] = useState(0);
  const [showBoxes, setShowBoxes] = useState(true);
  const canvasRef = useRef();

  const img = images[selIdx];

  const runDetection = useCallback(async (targets = null) => {
    const tgts = targets || [img];
    setStatus('loading'); setStatusMsg('Loading model…');
    try {
      let modelInst;
      if (model === 'mobilenet') { const mn = await getMobileNet(); modelInst = await mn.load(); }
      else { const cs = await getCocoSsd(); modelInst = await cs.load(); }
      setStatus('running');
      const newResults = {};
      for (let i = 0; i < tgts.length; i++) {
        setStatusMsg(`Running ${i + 1}/${tgts.length}…`); setBatchDone(i);
        await new Promise(r => setTimeout(r, 0));
        const el = new Image(); el.crossOrigin = 'anonymous'; el.src = tgts[i].url;
        await new Promise(r => { el.onload = r; if (el.complete) r(); });
        if (model === 'mobilenet') { const preds = await modelInst.classify(el, 5); newResults[tgts[i].id] = { labels: preds.map(p => ({ label: p.className, prob: +(p.probability * 100).toFixed(1) })) }; }
        else { const preds = await modelInst.detect(el); newResults[tgts[i].id] = { objects: preds.map(p => ({ label: p.class, score: +(p.score * 100).toFixed(1), bbox: p.bbox })) }; }
      }
      setResults(r => ({ ...r, ...newResults })); setStatus('done'); setStatusMsg(''); setBatchDone(0);
    } catch (e) { setStatus('error'); setStatusMsg(`Model error: ${e.message}`); }
  }, [img, model, images]);

  useEffect(() => {
    if (!canvasRef.current || !img) return;
    const res = results[img.id];
    if (!res?.objects || !showBoxes) return;
    const canvas = canvasRef.current;
    const el = new Image(); el.src = img.url;
    el.onload = () => {
      canvas.width = el.naturalWidth; canvas.height = el.naturalHeight;
      const ctx = canvas.getContext('2d'); ctx.drawImage(el, 0, 0);
      res.objects.forEach((obj, i) => {
        const [x, y, w, h] = obj.bbox, color = PALETTE[i % PALETTE.length];
        ctx.strokeStyle = color; ctx.lineWidth = 3; ctx.strokeRect(x, y, w, h);
        ctx.fillStyle = color + 'cc'; ctx.fillRect(x, y - 24, obj.label.length * 8 + 60, 24);
        ctx.fillStyle = '#fff'; ctx.font = 'bold 13px JetBrains Mono,monospace';
        ctx.fillText(`${obj.label} ${obj.score}%`, x + 6, y - 6);
      });
    };
  }, [img?.id, results, showBoxes]);

  const labelDist = useMemo(() => {
    const counts = {};
    Object.values(results).forEach(r => (r.labels || r.objects || []).slice(0, 1).forEach(l => { const key = l.label; counts[key] = (counts[key] || 0) + 1; }));
    return Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 20).map(([label, count]) => ({ label: label.split(',')[0].slice(0, 30), count }));
  }, [results]);

  const cur = img ? results[img.id] : null;
  const totalDetected = Object.keys(results).length;
  const running = status === 'loading' || status === 'running';

  if (!img) return <div style={{ padding: 60, textAlign: 'center', color: C.t4 }}>No images loaded.</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {images.length > 1 && <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4 }}>{images.map((m, i) => <ImgThumb key={m.id} img={m} selected={i === selIdx} onClick={() => setSelIdx(i)} size={60} badge={results[m.id] ? '✓' : undefined} badgeColor={C.green} />)}</div>}

      <Card>
        <Head label="MODEL SELECTION" accent={C.violet} />
        <Body>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
            {[
              ['mobilenet', 'MobileNetV2', 'Classification', 'Top-5 labels from 1000 ImageNet classes. Fast ~2s/image. Great for: products, animals, scenes, food.', C.cyan],
              ['cocoSsd',   'COCO-SSD',   'Object Detection', 'Bounding boxes + labels for 80 COCO classes. Locates multiple objects per image.', C.violet],
            ].map(([id, name, type, desc, color]) => (
              <div key={id} onClick={() => setModel(id)} style={{ padding: '12px 14px', background: model === id ? color + '10' : C.deep, border: `2px solid ${model === id ? color : C.b1}`, borderRadius: 4, cursor: 'pointer', transition: 'all .12s' }}>
                <div style={{ fontSize: 11, color, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700, marginBottom: 4 }}>{name}</div>
                <div style={{ fontSize: 9, color: C.t4, marginBottom: 6 }}>{type}</div>
                <div style={{ fontSize: 10, color: C.t2, lineHeight: 1.6 }}>{desc}</div>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <Btn onClick={() => runDetection()} color={C.violet} disabled={running}>{running ? `⟳ ${statusMsg}` : '▶ RUN ON THIS IMAGE'}</Btn>
            {images.length > 1 && <Btn onClick={() => runDetection(images)} color={C.cyan} disabled={running}>RUN ALL {images.length}</Btn>}
            {model === 'cocoSsd' && cur?.objects && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input type="checkbox" checked={showBoxes} onChange={e => setShowBoxes(e.target.checked)} style={{ accentColor: C.violet }} />
                <span style={{ fontSize: 9, color: C.t2 }}>Show bounding boxes</span>
              </label>
            )}
          </div>
          {running && images.length > 1 && <div style={{ marginTop: 8 }}><MBar pct={batchDone / images.length * 100} color={C.violet} h={5} /></div>}
          {status === 'error' && <div style={{ marginTop: 8, padding: '8px 12px', background: C.red + '10', border: `1px solid ${C.red}33`, borderRadius: 3, fontSize: 10, color: C.red }}>{statusMsg}</div>}
        </Body>
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Card>
          <Head label={cur?.objects && showBoxes ? 'DETECTION RESULT' : 'IMAGE PREVIEW'} accent={C.violet} />
          <Body style={{ padding: 10 }}>
            {cur?.objects && showBoxes
              ? <canvas ref={canvasRef} style={{ width: '100%', display: 'block', borderRadius: 3 }} />
              : <img src={img.url} alt="" style={{ width: '100%', maxHeight: 380, objectFit: 'contain', display: 'block', borderRadius: 3 }} />}
          </Body>
        </Card>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {cur?.labels && (
            <Card>
              <Head label="CLASSIFICATION — TOP 5" accent={C.cyan} />
              <Body>
                {cur.labels.map((l, i) => (
                  <div key={i} style={{ marginBottom: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                      <span style={{ fontSize: 11, color: i === 0 ? C.cyan : C.t2, fontWeight: i === 0 ? 700 : 400 }}>{l.label.split(',')[0]}</span>
                      <span style={{ fontSize: 10, color: i === 0 ? C.cyan : C.t3, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700 }}>{l.prob}%</span>
                    </div>
                    <MBar pct={l.prob} color={PALETTE[i]} h={4} />
                  </div>
                ))}
              </Body>
            </Card>
          )}
          {cur?.objects && (
            <Card>
              <Head label={`DETECTED OBJECTS — ${cur.objects.length}`} accent={C.violet} />
              <Body>
                {cur.objects.length === 0
                  ? <div style={{ color: C.t4, fontSize: 11 }}>No objects detected above threshold.</div>
                  : cur.objects.map((o, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '5px 0', borderBottom: `1px solid ${C.b0}` }}>
                      <div style={{ width: 10, height: 10, borderRadius: 2, background: PALETTE[i % PALETTE.length], flexShrink: 0 }} />
                      <span style={{ fontSize: 11, color: C.t1, flex: 1, fontWeight: 600 }}>{o.label}</span>
                      <span style={{ fontSize: 9, color: PALETTE[i % PALETTE.length], fontFamily: 'JetBrains Mono,monospace', fontWeight: 700 }}>{o.score}%</span>
                    </div>
                  ))}
              </Body>
            </Card>
          )}
          {!cur && <div style={{ padding: 40, textAlign: 'center', color: C.t4, fontSize: 11 }}>Run detection to see results here.</div>}
        </div>
      </div>

      {totalDetected > 1 && labelDist.length > 0 && (
        <Card>
          <Head label={`LABEL DISTRIBUTION — ${totalDetected} images analysed`} accent={C.violet} />
          <Body>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={labelDist} layout="vertical" barCategoryGap="10%" margin={{ left: 0, right: 60 }}>
                <CartesianGrid strokeDasharray="2 2" stroke={C.b0} horizontal={false} />
                <XAxis type="number" tick={{ fill: C.t4, fontSize: 8 }} />
                <YAxis dataKey="label" type="category" tick={{ fill: C.t2, fontSize: 10 }} width={160} />
                <Tooltip content={<ChartTip />} />
                <Bar dataKey="count" radius={[0, 3, 3, 0]}>{labelDist.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}</Bar>
              </BarChart>
            </ResponsiveContainer>
          </Body>
        </Card>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// ── 10  COMPARE TAB ──────────────────────────────────────────────────────────
// ═════════════════════════════════════════════════════════════════════════════

function CompareTab({ images }) {
  const [idxA, setIdxA] = useState(0);
  const [idxB, setIdxB] = useState(Math.min(1, images.length - 1));
  const [diffUrl, setDiffUrl] = useState(null);
  const [scores, setScores]   = useState(null);
  const [running, setRunning] = useState(false);
  const [matrixMode, setMatrixMode] = useState(false);
  const [matrix, setMatrix]   = useState(null);

  const compare = useCallback(async () => {
    if (!images[idxA] || !images[idxB]) return;
    setRunning(true);
    await new Promise(r => setTimeout(r, 10));
    const elA = new Image(), elB = new Image();
    elA.src = images[idxA].url; elB.src = images[idxB].url;
    await Promise.all([new Promise(r => { elA.onload = r; if (elA.complete) r(); }), new Promise(r => { elB.onload = r; if (elB.complete) r(); })]);
    const ssimScore = ssim(elA, elB);
    const hamming = hammingDist(images[idxA].hash || pHash(elA), images[idxB].hash || pHash(elB));
    const similarity = +(100 - hamming / 64 * 100).toFixed(1);
    const ca = document.createElement('canvas'), SIZE = 256; ca.width = SIZE; ca.height = SIZE;
    const ctx = ca.getContext('2d');
    ctx.drawImage(elA, 0, 0, SIZE, SIZE); const dA = ctx.getImageData(0, 0, SIZE, SIZE).data;
    ctx.drawImage(elB, 0, 0, SIZE, SIZE); const dB = ctx.getImageData(0, 0, SIZE, SIZE).data;
    const diff = new Uint8ClampedArray(dA.length); let totalDiff = 0;
    for (let i = 0; i < dA.length; i += 4) {
      const dr = Math.abs(dA[i]-dB[i]), dg = Math.abs(dA[i+1]-dB[i+1]), db = Math.abs(dA[i+2]-dB[i+2]);
      totalDiff += dr + dg + db;
      diff[i] = Math.min(255, dr*3); diff[i+1] = Math.min(255, dg*3); diff[i+2] = Math.min(255, db*3); diff[i+3] = 255;
    }
    ctx.putImageData(new ImageData(diff, SIZE, SIZE), 0, 0);
    setDiffUrl(ca.toDataURL()); setScores({ ssimScore, similarity, hamming, mse: +(totalDiff / (SIZE*SIZE*3*255)*100).toFixed(2) });
    setRunning(false);
  }, [images, idxA, idxB]);

  const buildMatrix = useCallback(async () => {
    const N = Math.min(images.length, 30); setRunning(true);
    const els = [];
    for (let i = 0; i < N; i++) { const el = new Image(); el.src = images[i].url; await new Promise(r => { el.onload = r; if (el.complete) r(); }); els.push(el); }
    const mat = [];
    for (let i = 0; i < N; i++) {
      const row = [];
      for (let j = 0; j < N; j++) { if (i===j){row.push(1);continue;} const h = hammingDist(images[i].hash||pHash(els[i]), images[j].hash||pHash(els[j])); row.push(+(1-h/64).toFixed(3)); }
      mat.push(row);
    }
    setMatrix({ mat, n: N, labels: images.slice(0, N).map(i => i.name.slice(0, 12)) }); setRunning(false);
  }, [images]);

  if (images.length === 0) return <div style={{ padding: 60, textAlign: 'center', color: C.t4 }}>No images loaded.</div>;
  if (images.length === 1) return <Info color={C.gold}>Load at least 2 images to use Compare.</Info>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', gap: 4 }}>
        {[['pair', 'PAIR COMPARE'], ['matrix', 'SIMILARITY MATRIX']].map(([v, l]) => (
          <button key={v} onClick={() => setMatrixMode(v === 'matrix')} style={{ padding: '6px 14px', border: `1px solid ${matrixMode === (v === 'matrix') ? C.cyan : C.b0}`, background: matrixMode === (v === 'matrix') ? C.cyan + '15' : C.raised, color: matrixMode === (v === 'matrix') ? C.cyan : C.t2, fontSize: 9, cursor: 'pointer', borderRadius: 3, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700 }}>{l}</button>
        ))}
      </div>

      {!matrixMode && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            {[['A', idxA, setIdxA, C.green], ['B', idxB, setIdxB, C.cyan]].map(([lbl, idx, setIdx, color]) => (
              <Card key={lbl}>
                <Head label={`IMAGE ${lbl}`} accent={color} />
                <Body style={{ padding: 8 }}>
                  <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4 }}>
                    {images.map((m, i) => <ImgThumb key={m.id} img={m} selected={i === idx} onClick={() => setIdx(i)} size={52} />)}
                  </div>
                  {images[idx] && <div style={{ marginTop: 8, background: C.deep, borderRadius: 3, overflow: 'hidden' }}><img src={images[idx].url} style={{ width: '100%', maxHeight: 200, objectFit: 'contain', display: 'block' }} /></div>}
                </Body>
              </Card>
            ))}
          </div>
          <Btn onClick={compare} color={C.cyan} disabled={running || idxA === idxB}>{running ? '⟳ COMPARING…' : '⚖ COMPARE'}</Btn>
          {scores && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10 }}>
              <MB label="PHASH SIMILARITY" value={`${scores.similarity}%`} color={scores.similarity > 90 ? C.green : scores.similarity > 70 ? C.gold : C.red} sub={`${scores.hamming}/64 bits`} />
              <MB label="SSIM SCORE"       value={scores.ssimScore}        color={scores.ssimScore > .8 ? C.green : scores.ssimScore > .5 ? C.gold : C.red} sub="1.0 = identical" />
              <MB label="PIXEL MSE"        value={`${scores.mse}%`}        color={scores.mse < 5 ? C.green : scores.mse < 15 ? C.gold : C.red} />
              <MB label="VERDICT"          value={scores.similarity > 90 ? 'NEAR DUPE' : scores.similarity > 70 ? 'SIMILAR' : scores.similarity > 50 ? 'RELATED' : 'DIFFERENT'} color={scores.similarity > 90 ? C.green : scores.similarity > 70 ? C.gold : C.red} />
            </div>
          )}
          {diffUrl && (
            <Card>
              <Head label="PIXEL DIFFERENCE MAP — amplified ×3" accent={C.cyan} sub="Bright pixels = large differences between images" />
              <Body>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                  {[['IMAGE A', images[idxA]?.url], ['PIXEL DIFF', diffUrl], ['IMAGE B', images[idxB]?.url]].map(([l, src]) => (
                    <div key={l}><div style={{ fontSize: 8, color: C.t4, fontFamily: 'JetBrains Mono,monospace', textAlign: 'center', marginBottom: 4 }}>{l}</div><img src={src} style={{ width: '100%', display: 'block', borderRadius: 3 }} /></div>
                  ))}
                </div>
              </Body>
            </Card>
          )}
        </>
      )}

      {matrixMode && (
        <>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Btn onClick={buildMatrix} color={C.cyan} disabled={running}>{running ? '⟳ BUILDING…' : '▶ BUILD MATRIX'}</Btn>
            <span style={{ fontSize: 10, color: C.t3 }}>Compares up to 30 images via perceptual hash. O(n²) = {Math.min(images.length, 30) ** 2} comparisons.</span>
          </div>
          {matrix && (
            <Card>
              <Head label={`SIMILARITY MATRIX — ${matrix.n}×${matrix.n}`} accent={C.cyan} sub="1.0 = identical · 0.0 = completely different" />
              <Body style={{ overflowX: 'auto', padding: 0 }}>
                <table style={{ borderCollapse: 'collapse' }}>
                  <thead><tr><th style={{ width: 100, padding: '4px 6px', fontSize: 7, color: C.t4 }} />{matrix.labels.map((l, j) => <th key={j} style={{ padding: '4px 6px', fontSize: 7, color: C.t3, fontFamily: 'JetBrains Mono,monospace', whiteSpace: 'nowrap', transform: 'rotate(-45deg)', transformOrigin: 'bottom left', height: 60 }}>{l}</th>)}</tr></thead>
                  <tbody>{matrix.mat.map((row, i) => (
                    <tr key={i}><td style={{ padding: '3px 6px', fontSize: 8, color: C.t3, fontFamily: 'JetBrains Mono,monospace', whiteSpace: 'nowrap' }}>{matrix.labels[i]}</td>
                      {row.map((val, j) => {
                        const bg = val > .9 ? '#4ade8090' : val > .7 ? '#fbbf2480' : val > .5 ? '#fb923c40' : '#f8717120';
                        const fc = val > .9 ? C.green : val > .7 ? C.gold : val > .5 ? C.orange : C.red;
                        return <td key={j} style={{ padding: '3px 6px', background: bg, textAlign: 'center', fontSize: 8, color: i === j ? C.t4 : fc, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700, border: `1px solid ${C.b0}` }}>{val.toFixed(2)}</td>;
                      })}
                    </tr>
                  ))}</tbody>
                </table>
              </Body>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// ── 11  CLUSTER TAB ──────────────────────────────────────────────────────────
// ═════════════════════════════════════════════════════════════════════════════

function ClusterTab({ images }) {
  const [k, setK] = useState(5);
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);

  const run = useCallback(async () => {
    setRunning(true);
    await new Promise(r => setTimeout(r, 20));
    const fps = images.map(i => i.fp);
    const labels = clusterImages(fps, k);
    const pca = pca2d(fps);
    const clusters = Array.from({ length: k }, (_, ci) => ({
      id: ci, color: PALETTE[ci % PALETTE.length],
      images: images.filter((_, i) => labels[i] === ci),
      indices: labels.reduce((a, l, i) => l === ci ? [...a, i] : a, []),
    }));
    setResult({ labels, pca, clusters });
    setRunning(false);
  }, [images, k]);

  if (images.length < 2) return <Info color={C.violet}>Load at least 2 images to cluster.</Info>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <Card>
        <Head label="VISUAL CLUSTERING — K-MEANS ON PIXEL FINGERPRINTS" accent={C.violet} />
        <Body>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 9, color: C.t3, fontFamily: 'JetBrains Mono,monospace' }}>K CLUSTERS:</span>
            {[2, 3, 4, 5, 6, 8, 10, 15, 20].filter(n => n <= images.length).map(n => (
              <button key={n} onClick={() => setK(n)} style={{ padding: '5px 10px', border: `1px solid ${k === n ? C.violet : C.b1}`, background: k === n ? C.violet + '18' : 'transparent', color: k === n ? C.violet : C.t3, fontSize: 9, borderRadius: 3, cursor: 'pointer', fontFamily: 'JetBrains Mono,monospace', fontWeight: 700 }}>{n}</button>
            ))}
            <Btn onClick={run} color={C.violet} disabled={running}>{running ? '⟳ CLUSTERING…' : '▶ RUN'}</Btn>
          </div>
          <div style={{ marginTop: 8, fontSize: 10, color: C.t3 }}>192-dim pixel fingerprints (8×8 blocks × 3 channels). Pure-JS k-means++ with PCA 2D visualization. No data leaves your browser.</div>
        </Body>
      </Card>

      {result && (
        <>
          <Card>
            <Head label="PCA 2D SCATTER — visual fingerprint space" accent={C.violet} sub="Each dot = one image, colored by cluster" />
            <Body>
              <ResponsiveContainer width="100%" height={360}>
                <ScatterChart margin={{ top: 10, right: 20, bottom: 20, left: 0 }}>
                  <CartesianGrid strokeDasharray="2 2" stroke={C.b0} />
                  <XAxis dataKey="x" name="PC1" tick={{ fill: C.t4, fontSize: 8 }} label={{ value: 'PC1', position: 'insideBottom', offset: -10, fill: C.t4, fontSize: 9 }} />
                  <YAxis dataKey="y" name="PC2" tick={{ fill: C.t4, fontSize: 8 }} label={{ value: 'PC2', angle: -90, position: 'insideLeft', fill: C.t4, fontSize: 9 }} />
                  <Tooltip content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const d = payload[0]?.payload;
                    return <div style={{ background: C.surface, border: `1px solid ${C.b2}`, padding: '6px 10px', borderRadius: 3, fontSize: 10 }}><div style={{ color: d?.color || C.cyan }}>{d?.name}</div><div style={{ color: C.t3 }}>Cluster {(d?.cluster || 0) + 1}</div></div>;
                  }} />
                  {result.clusters.map(cl => (
                    <Scatter key={cl.id} data={cl.indices.map(i => ({ x: result.pca[i].x, y: result.pca[i].y, name: images[i].name, cluster: cl.id, color: cl.color }))} fill={cl.color} opacity={0.85} name={`Cluster ${cl.id + 1}`} />
                  ))}
                  <Legend wrapperStyle={{ fontSize: 9 }} />
                </ScatterChart>
              </ResponsiveContainer>
            </Body>
          </Card>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(260px,1fr))', gap: 12 }}>
            {result.clusters.map(cl => (
              <Card key={cl.id}>
                <Head label={`CLUSTER ${cl.id + 1} — ${cl.images.length} IMAGES`} accent={cl.color} sub={`${(cl.images.length / images.length * 100).toFixed(1)}% of corpus`} />
                <Body>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 10 }}>
                    {cl.images.slice(0, 12).map(im => (
                      <div key={im.id} style={{ width: 48, height: 48, borderRadius: 2, overflow: 'hidden', border: `1px solid ${cl.color}44` }}>
                        <img src={im.url} alt={im.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </div>
                    ))}
                    {cl.images.length > 12 && <div style={{ width: 48, height: 48, borderRadius: 2, background: C.raised, border: `1px solid ${C.b1}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: C.t4 }}>+{cl.images.length - 12}</div>}
                  </div>
                  <MBar pct={cl.images.length / images.length * 100} color={cl.color} h={6} />
                </Body>
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// ── 12  SIMILAR TAB ──────────────────────────────────────────────────────────
// ═════════════════════════════════════════════════════════════════════════════

function SimilarTab({ images }) {
  const [queryIdx, setQueryIdx] = useState(0);
  const [topN, setTopN] = useState(12);
  const [results, setResults] = useState(null);

  const search = useCallback(() => {
    const query = images[queryIdx]; if (!query) return;
    const scores = images.map((img, i) => {
      if (i === queryIdx) return null;
      const cos = cosineSim(query.fp, img.fp);
      const hd  = hammingDist(query.hash || '', img.hash || '');
      return { img, score: +(cos * .7 + (1 - hd / 64) * .3).toFixed(4), dist: hd };
    }).filter(Boolean).sort((a, b) => b.score - a.score);
    setResults(scores);
  }, [images, queryIdx]);

  if (images.length < 2) return <Info color={C.green}>Load at least 2 images to use similarity search.</Info>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <Card>
        <Head label="VISUAL SIMILARITY SEARCH — query by image" accent={C.green} sub="70% cosine similarity + 30% perceptual hash" />
        <Body>
          <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 14 }}>
            <div>
              <Label>QUERY IMAGE</Label>
              <div style={{ background: C.deep, borderRadius: 3, overflow: 'hidden', marginBottom: 10, border: `2px solid ${C.green}44` }}>
                <img src={images[queryIdx]?.url} alt="" style={{ width: '100%', maxHeight: 180, objectFit: 'contain', display: 'block' }} />
              </div>
              <select value={queryIdx} onChange={e => setQueryIdx(+e.target.value)} style={{ width: '100%', background: C.deep, border: `1px solid ${C.b2}`, color: C.t1, padding: '6px 8px', fontSize: 10, fontFamily: 'JetBrains Mono,monospace', borderRadius: 3, outline: 'none', marginBottom: 10 }}>
                {images.map((img, i) => <option key={img.id} value={i}>{img.name}</option>)}
              </select>
              <div style={{ marginBottom: 10 }}>
                <Label>TOP N</Label>
                <div style={{ display: 'flex', gap: 6 }}>
                  {[6, 12, 20, 50].map(n => <button key={n} onClick={() => setTopN(n)} style={{ padding: '3px 8px', border: `1px solid ${topN === n ? C.green : C.b1}`, background: topN === n ? C.green + '18' : 'transparent', color: topN === n ? C.green : C.t3, fontSize: 9, borderRadius: 2, cursor: 'pointer', fontFamily: 'JetBrains Mono,monospace' }}>{n}</button>)}
                </div>
              </div>
              <Btn onClick={search} color={C.green}>🔍 SEARCH</Btn>
            </div>
            <div>
              {results && (
                <>
                  <div style={{ fontSize: 9, color: C.t3, fontFamily: 'JetBrains Mono,monospace', marginBottom: 10 }}>TOP {Math.min(topN, results.length)} RESULTS — sorted by visual similarity</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {results.slice(0, topN).map((r, i) => (
                      <div key={r.img.id} style={{ width: 80 }}>
                        <div style={{ borderRadius: 3, overflow: 'hidden', border: `2px solid ${r.score > .8 ? C.green : r.score > .6 ? C.gold : C.b1}`, marginBottom: 4 }}>
                          <img src={r.img.url} alt="" style={{ width: '100%', height: 80, objectFit: 'cover', display: 'block' }} />
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: 7, color: C.t4, fontFamily: 'JetBrains Mono,monospace' }}>#{i + 1}</span>
                          <span style={{ fontSize: 8, color: r.score > .8 ? C.green : r.score > .6 ? C.gold : C.t3, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700 }}>{(r.score * 100).toFixed(0)}%</span>
                        </div>
                        <MBar pct={r.score * 100} color={r.score > .8 ? C.green : r.score > .6 ? C.gold : C.t3} h={3} />
                        <div style={{ fontSize: 7, color: C.t4, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.img.name}</div>
                      </div>
                    ))}
                  </div>
                </>
              )}
              {!results && <div style={{ color: C.t4, fontSize: 11, padding: '40px 20px' }}>Select a query image and click SEARCH.</div>}
            </div>
          </div>
        </Body>
      </Card>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// ── 13  QUALITY TAB — Dataset Health Scanner ─────────────────────────────────
// ═════════════════════════════════════════════════════════════════════════════

function QualityTab({ images }) {
  const [running, setRunning]         = useState(false);
  const [results, setResults]         = useState(null);
  const [sortBy, setSortBy]           = useState('score');
  const [dupeThreshold, setDupeThresh] = useState(8);

  const run = useCallback(async () => {
    setRunning(true);
    await new Promise(r => setTimeout(r, 20));
    const items = [];
    for (let i = 0; i < images.length; i++) {
      const img = images[i];
      const el = new Image(); el.src = img.url;
      await new Promise(r => { el.onload = r; if (el.complete) r(); });
      const blur    = sharpness(el);
      const entropy = imageEntropy(el);
      const noise   = noiseEstimate(el);
      const clip    = exposureClipping(el);
      const temp    = colorTemp(el);
      const hash    = pHash(el);
      const score   = qualityScore(blur, clip.clipped, noise);
      items.push({ id: img.id, name: img.name, url: img.url, blur, entropy, noise, clip, temp, hash, score });
    }
    // Near-duplicate detection — O(n²) on corpus
    const dupes = [];
    for (let i = 0; i < items.length; i++) {
      for (let j = i + 1; j < items.length; j++) {
        const hd = hammingDist(items[i].hash, items[j].hash);
        if (hd <= dupeThreshold) dupes.push({ a: i, b: j, hd, similarity: +(100 - hd / 64 * 100).toFixed(1) });
      }
    }
    setResults({ items, dupes });
    setRunning(false);
  }, [images, dupeThreshold]);

  if (images.length === 0) return <div style={{ padding: 60, textAlign: 'center', color: C.t4 }}>No images loaded.</div>;

  const blurBuckets = results ? [
    { name: 'BLURRY\n<100',    count: results.items.filter(r => r.blur < 100).length,              fill: '#f87171' },
    { name: 'SOFT\n100-500',   count: results.items.filter(r => r.blur >= 100 && r.blur < 500).length, fill: '#fbbf24' },
    { name: 'GOOD\n500-2k',    count: results.items.filter(r => r.blur >= 500 && r.blur < 2000).length, fill: '#22d3ee' },
    { name: 'CRISP\n>2000',    count: results.items.filter(r => r.blur >= 2000).length,            fill: '#4ade80' },
  ] : [];

  const noiseBuckets = results ? [
    { name: '<5 CLEAN',   count: results.items.filter(r => r.noise < 5).length,                fill: '#4ade80' },
    { name: '5-15 MOD',   count: results.items.filter(r => r.noise >= 5 && r.noise < 15).length, fill: '#fbbf24' },
    { name: '>15 NOISY',  count: results.items.filter(r => r.noise >= 15).length,               fill: '#f87171' },
  ] : [];

  const avgScore   = results ? +(results.items.reduce((s, r) => s + r.score, 0) / results.items.length).toFixed(1) : 0;
  const blurIssues = results ? results.items.filter(r => r.blur < 100).length   : 0;
  const clipIssues = results ? results.items.filter(r => r.clip.clipped > 20).length : 0;

  const sorted = results ? [...results.items].sort((a, b) =>
    sortBy === 'score'  ? a.score       - b.score :
    sortBy === 'blur'   ? a.blur        - b.blur  :
    sortBy === 'clip'   ? b.clip.clipped - a.clip.clipped :
    b.noise - a.noise
  ) : [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Controls */}
      <Card>
        <Head label="DATASET HEALTH SCANNER" accent={C.green} sub="Blur · Noise · Exposure · Near-Duplicates — 100% in-browser" />
        <Body>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
            <span style={{ fontSize: 9, color: C.t3, fontFamily: 'JetBrains Mono,monospace' }}>DUPE HASH DISTANCE ≤</span>
            {[4, 6, 8, 10, 12].map(v => (
              <button key={v} onClick={() => setDupeThresh(v)} style={{ padding: '3px 8px', border: `1px solid ${dupeThreshold === v ? C.green : C.b1}`, background: dupeThreshold === v ? C.green + '18' : 'transparent', color: dupeThreshold === v ? C.green : C.t3, fontSize: 9, borderRadius: 2, cursor: 'pointer', fontFamily: 'JetBrains Mono,monospace', fontWeight: dupeThreshold === v ? 700 : 400 }}>{v}</button>
            ))}
            <span style={{ fontSize: 8, color: C.t4 }}>bits (lower = stricter)</span>
          </div>
          <Btn onClick={run} color={C.green} disabled={running}>{running ? `⟳ SCANNING ${images.length} IMAGES…` : `▶ SCAN DATASET (${images.length} images)`}</Btn>
        </Body>
      </Card>

      {results && (
        <>
          {/* KPI row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10 }}>
            <MB label="AVG QUALITY SCORE" value={`${avgScore}/100`}     color={avgScore > 70 ? C.green : avgScore > 50 ? C.gold : C.red}    sub={`${images.length} images scored`} />
            <MB label="BLURRY IMAGES"     value={blurIssues}             color={blurIssues === 0 ? C.green : C.red}                          sub="Laplacian var < 100" />
            <MB label="EXPOSURE ISSUES"   value={clipIssues}             color={clipIssues === 0 ? C.green : C.gold}                         sub="Clipped pixels > 20%" />
            <MB label="NEAR-DUPLICATE PAIRS" value={results.dupes.length} color={results.dupes.length === 0 ? C.green : C.orange}            sub={`hash distance ≤ ${dupeThreshold}`} />
          </div>

          {/* Charts row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Card>
              <Head label="SHARPNESS DISTRIBUTION" accent={C.gold} sub="Laplacian variance — higher = sharper" />
              <Body>
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={blurBuckets} barSize={44} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
                    <CartesianGrid strokeDasharray="2 2" stroke={C.b0} />
                    <XAxis dataKey="name" tick={{ fill: C.t3, fontSize: 8 }} />
                    <YAxis tick={{ fill: C.t4, fontSize: 8 }} allowDecimals={false} />
                    <Tooltip content={<ChartTip />} />
                    <Bar dataKey="count" radius={[3, 3, 0, 0]}>
                      {blurBuckets.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Body>
            </Card>
            <Card>
              <Head label="NOISE LEVEL DISTRIBUTION" accent={C.violet} sub="std-dev of local residuals — lower = cleaner" />
              <Body>
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={noiseBuckets} barSize={52} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
                    <CartesianGrid strokeDasharray="2 2" stroke={C.b0} />
                    <XAxis dataKey="name" tick={{ fill: C.t3, fontSize: 8 }} />
                    <YAxis tick={{ fill: C.t4, fontSize: 8 }} allowDecimals={false} />
                    <Tooltip content={<ChartTip />} />
                    <Bar dataKey="count" radius={[3, 3, 0, 0]}>
                      {noiseBuckets.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Body>
            </Card>
          </div>

          {/* Near-duplicate pairs */}
          {results.dupes.length > 0 && (
            <Card>
              <Head label={`NEAR-DUPLICATE PAIRS — ${results.dupes.length} found`} accent={C.orange} sub="Perceptual hash (pHash) comparison · consider deduplication" />
              <Body style={{ padding: 8 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {results.dupes.slice(0, 20).map((d, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px', background: C.deep, borderRadius: 3, border: `1px solid ${C.orange}22` }}>
                      <img src={results.items[d.a].url} alt="" style={{ width: 52, height: 52, objectFit: 'cover', borderRadius: 2, flexShrink: 0 }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 9, color: C.t2, fontFamily: 'JetBrains Mono,monospace', marginBottom: 3 }}>{results.items[d.a].name}</div>
                        <div style={{ fontSize: 10, color: C.orange, fontWeight: 700, fontFamily: 'JetBrains Mono,monospace' }}>{d.similarity}% SIMILAR · Δhash={d.hd} bits</div>
                      </div>
                      <img src={results.items[d.b].url} alt="" style={{ width: 52, height: 52, objectFit: 'cover', borderRadius: 2, flexShrink: 0 }} />
                      <div style={{ fontSize: 9, color: C.t2, fontFamily: 'JetBrains Mono,monospace' }}>{results.items[d.b].name}</div>
                    </div>
                  ))}
                  {results.dupes.length > 20 && <div style={{ fontSize: 9, color: C.t4, textAlign: 'center', padding: 6 }}>… and {results.dupes.length - 20} more pairs</div>}
                </div>
              </Body>
            </Card>
          )}

          {/* Per-image quality table */}
          <Card>
            <Head
              label="PER-IMAGE QUALITY SCORES"
              accent={C.green}
              right={
                <div style={{ display: 'flex', gap: 4 }}>
                  {[['score', 'SCORE ▲'], ['blur', 'SHARP ▲'], ['clip', 'CLIP ▼'], ['noise', 'NOISE ▼']].map(([v, l]) => (
                    <button key={v} onClick={() => setSortBy(v)} style={{ padding: '2px 7px', border: `1px solid ${sortBy === v ? C.green : C.b0}`, background: sortBy === v ? C.green + '18' : C.deep, color: sortBy === v ? C.green : C.t4, fontSize: 8, borderRadius: 2, cursor: 'pointer', fontFamily: 'JetBrains Mono,monospace' }}>{l}</button>
                  ))}
                </div>
              }
            />
            <Body style={{ padding: 0, overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {[['IMAGE', ''], ['SCORE', C.green], ['SHARPNESS', C.gold], ['CLIPPED %', C.orange], ['NOISE', C.violet], ['ENTROPY', C.cyan], ['COLOR TEMP', C.t2], ['FLAGS', '']].map(([h, c]) => (
                      <th key={h} style={{ ...TH, color: c || C.t3 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((r, i) => {
                    const flags = [];
                    if (r.blur < 100)         flags.push({ label: 'BLURRY',   c: C.red });
                    if (r.clip.clipped > 20)  flags.push({ label: 'CLIPPED',  c: C.orange });
                    if (r.clip.shadows > 30)  flags.push({ label: 'DARK',     c: C.violet });
                    if (r.noise > 15)         flags.push({ label: 'NOISY',    c: C.gold });
                    if (r.entropy < 3)        flags.push({ label: 'FLAT',     c: C.t3 });
                    return (
                      <tr key={r.id} style={{ borderBottom: `1px solid ${C.b0}`, background: i % 2 ? C.raised + '50' : 'transparent' }}>
                        <td style={{ ...TD, display: 'flex', alignItems: 'center', gap: 6, padding: '7px 10px' }}>
                          <img src={r.url} alt="" style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 2, flexShrink: 0 }} />
                          <span style={{ fontSize: 9, color: C.t2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 110 }}>{r.name}</span>
                        </td>
                        <td style={TD}><span style={{ color: r.score > 70 ? C.green : r.score > 50 ? C.gold : C.red, fontWeight: 700, fontSize: 13 }}>{r.score}</span></td>
                        <td style={TD}><span style={{ color: r.blur > 2000 ? C.green : r.blur > 500 ? C.cyan : r.blur > 100 ? C.gold : C.red }}>{r.blur.toFixed(0)}</span></td>
                        <td style={TD}><span style={{ color: r.clip.clipped < 5 ? C.green : r.clip.clipped < 20 ? C.gold : C.red }}>{r.clip.clipped}%</span></td>
                        <td style={TD}><span style={{ color: r.noise < 5 ? C.green : r.noise < 15 ? C.gold : C.red }}>{r.noise}</span></td>
                        <td style={TD}>{r.entropy.toFixed(2)}</td>
                        <td style={{ ...TD }}><span style={{ color: r.temp.color, fontWeight: 700 }}>{r.temp.label}</span></td>
                        <td style={{ ...TD, padding: '7px 10px' }}>
                          <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                            {flags.length === 0
                              ? <span style={{ fontSize: 8, color: C.green }}>✓ OK</span>
                              : flags.map(f => <span key={f.label} style={{ fontSize: 7, padding: '1px 5px', border: `1px solid ${f.c}44`, color: f.c, borderRadius: 2, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700, whiteSpace: 'nowrap' }}>{f.label}</span>)
                            }
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Body>
          </Card>
        </>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// ── 14  EMBED TAB — Embedding Atlas ──────────────────────────────────────────
// ═════════════════════════════════════════════════════════════════════════════

function EmbedTab({ images }) {
  const [running, setRunning] = useState(false);
  const [result, setResult]   = useState(null);
  const [colorBy, setColorBy] = useState('index');
  const [selIdx, setSelIdx]   = useState(null);

  const run = useCallback(async () => {
    setRunning(true);
    await new Promise(r => setTimeout(r, 20));
    const vecs   = images.map(img => img.fp || new Array(192).fill(0));
    const coords = pca2d(vecs);
    setResult({ coords });
    setRunning(false);
  }, [images]);

  if (images.length < 3) return <Info color={C.violet}>Load at least 3 images to build an embedding atlas.</Info>;

  const getColor = (i) => {
    if (colorBy === 'index') return PALETTE[i % PALETTE.length];
    const b = images[i]?.metrics?.brightness ?? 50;
    const s = images[i]?.metrics?.sharpness  ?? 500;
    if (colorBy === 'brightness') return b > 70 ? C.green : b > 40 ? C.gold : C.violet;
    if (colorBy === 'sharpness')  return s > 2000 ? C.green : s > 500 ? C.cyan : s > 100 ? C.gold : C.red;
    return C.cyan;
  };

  const scatterData = result
    ? images.map((img, i) => ({ x: result.coords[i].x, y: result.coords[i].y, name: img.name, idx: i, color: getColor(i) }))
    : [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <Card>
        <Head label="EMBEDDING ATLAS — PCA 2D PROJECTION" accent={C.violet} sub="192-dim visual fingerprints projected to 2D. Visual clusters = similar-looking images." />
        <Body>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <Btn onClick={run} color={C.violet} disabled={running}>{running ? '⟳ PROJECTING…' : '▶ BUILD ATLAS'}</Btn>
            <span style={{ fontSize: 9, color: C.t3, fontFamily: 'JetBrains Mono,monospace', marginLeft: 6 }}>COLOUR BY:</span>
            {[['index', 'IMAGE INDEX'], ['brightness', 'BRIGHTNESS'], ['sharpness', 'SHARPNESS']].map(([v, l]) => (
              <button key={v} onClick={() => { setColorBy(v); }} style={{ padding: '3px 8px', border: `1px solid ${colorBy === v ? C.violet : C.b1}`, background: colorBy === v ? C.violet + '18' : 'transparent', color: colorBy === v ? C.violet : C.t3, fontSize: 9, borderRadius: 2, cursor: 'pointer', fontFamily: 'JetBrains Mono,monospace', fontWeight: colorBy === v ? 700 : 400 }}>{l}</button>
            ))}
          </div>
          <div style={{ marginTop: 8, fontSize: 9, color: C.t4 }}>Uses the same 192-dim pixel fingerprints as Cluster tab (8×8 spatial blocks × 3 RGB channels). Power-iteration PCA, no server calls.</div>
        </Body>
      </Card>

      {result && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 220px', gap: 14 }}>
          <Card>
            <Head label={`ATLAS — ${images.length} IMAGES`} accent={C.violet} sub="Each dot = one image · click dot to inspect" />
            <Body style={{ padding: 10 }}>
              <ResponsiveContainer width="100%" height={480}>
                <ScatterChart margin={{ top: 10, right: 10, bottom: 28, left: 0 }}>
                  <CartesianGrid strokeDasharray="2 2" stroke={C.b0} />
                  <XAxis dataKey="x" name="PC1" tick={{ fill: C.t4, fontSize: 8 }} label={{ value: 'PC1 →', position: 'insideBottom', offset: -12, fill: C.t4, fontSize: 9 }} />
                  <YAxis dataKey="y" name="PC2" tick={{ fill: C.t4, fontSize: 8 }} label={{ value: '↑ PC2', angle: -90, position: 'insideLeft', fill: C.t4, fontSize: 9 }} />
                  <Tooltip content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const d = payload[0]?.payload;
                    return (
                      <div style={{ background: C.surface, border: `1px solid ${C.b2}`, padding: '6px 10px', borderRadius: 3, fontSize: 10, maxWidth: 180 }}>
                        <div style={{ color: d?.color, fontWeight: 700, marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d?.name}</div>
                        <div style={{ color: C.t3 }}>PC1: {d?.x?.toFixed(3)}</div>
                        <div style={{ color: C.t3 }}>PC2: {d?.y?.toFixed(3)}</div>
                      </div>
                    );
                  }} />
                  <Scatter data={scatterData} onClick={d => setSelIdx(d?.idx ?? null)} style={{ cursor: 'pointer' }}>
                    {scatterData.map((d, i) => (
                      <Cell key={i} fill={d.color} stroke={selIdx === i ? '#ffffff' : 'transparent'} strokeWidth={2} r={selIdx === i ? 7 : 5} />
                    ))}
                  </Scatter>
                </ScatterChart>
              </ResponsiveContainer>
            </Body>
          </Card>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <Card>
              <Head label="IMAGE DETAIL" accent={C.violet} />
              <Body style={{ padding: 8 }}>
                {selIdx != null && images[selIdx] ? (
                  <>
                    <img src={images[selIdx].url} alt="" style={{ width: '100%', maxHeight: 140, objectFit: 'contain', borderRadius: 3, display: 'block', marginBottom: 8 }} />
                    <div style={{ fontSize: 9, color: C.t1, fontFamily: 'JetBrains Mono,monospace', marginBottom: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{images[selIdx].name}</div>
                    <div style={{ fontSize: 8, color: C.t4, lineHeight: 1.7 }}>
                      <div>PC1: {result.coords[selIdx].x.toFixed(4)}</div>
                      <div>PC2: {result.coords[selIdx].y.toFixed(4)}</div>
                      <div style={{ marginTop: 4 }}>Brightness: {(images[selIdx].metrics?.brightness ?? 0).toFixed(1)}%</div>
                      <div>Contrast: {(images[selIdx].metrics?.contrast ?? 0).toFixed(1)}%</div>
                      <div>Sharpness: {(images[selIdx].metrics?.sharpness ?? 0).toFixed(0)}</div>
                      <div>Size: {images[selIdx].width}×{images[selIdx].height}</div>
                    </div>
                  </>
                ) : (
                  <div style={{ color: C.t4, fontSize: 10, textAlign: 'center', padding: '24px 10px', lineHeight: 1.7 }}>Click a dot on the scatter plot to inspect that image.</div>
                )}
              </Body>
            </Card>

            <Card>
              <Head label="COLOUR LEGEND" accent={C.violet} />
              <Body style={{ padding: 10 }}>
                {colorBy === 'index'      && <div style={{ fontSize: 9, color: C.t4, lineHeight: 1.7 }}>Each image index maps to a palette colour. Images nearby in 2D space share similar visual fingerprints.</div>}
                {colorBy === 'brightness' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {[[C.green, 'BRIGHT', '> 70%'], [C.gold, 'MID', '40–70%'], [C.violet, 'DARK', '< 40%']].map(([c, l, r]) => (
                      <div key={l} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 10, height: 10, borderRadius: '50%', background: c, flexShrink: 0 }} />
                        <span style={{ fontSize: 9, color: C.t3 }}>{l} — {r}</span>
                      </div>
                    ))}
                  </div>
                )}
                {colorBy === 'sharpness' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {[[C.green, 'CRISP', '> 2000'], [C.cyan, 'GOOD', '500–2000'], [C.gold, 'SOFT', '100–500'], [C.red, 'BLURRY', '< 100']].map(([c, l, r]) => (
                      <div key={l} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 10, height: 10, borderRadius: '50%', background: c, flexShrink: 0 }} />
                        <span style={{ fontSize: 9, color: C.t3 }}>{l} — {r}</span>
                      </div>
                    ))}
                  </div>
                )}
              </Body>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// ── 15  ANOMALY TAB — Outlier Detection ──────────────────────────────────────
// ═════════════════════════════════════════════════════════════════════════════

function AnomalyTab({ images }) {
  const [running, setRunning]   = useState(false);
  const [result, setResult]     = useState(null);
  const [threshold, setThreshold] = useState(2.5);

  const METRICS = [
    { key: 'brightness', label: 'BRIGHTNESS', color: C.gold   },
    { key: 'sharpness',  label: 'SHARPNESS',  color: C.cyan   },
    { key: 'contrast',   label: 'CONTRAST',   color: C.violet },
    { key: 'fileSize',   label: 'FILE SIZE',  color: C.orange },
  ];

  const run = useCallback(async () => {
    setRunning(true);
    await new Promise(r => setTimeout(r, 20));
    const items = images.map(img => ({
      id:         img.id,
      name:       img.name,
      url:        img.url,
      brightness: img.metrics?.brightness ?? 50,
      sharpness:  img.metrics?.sharpness  ?? 500,
      contrast:   img.metrics?.contrast   ?? 30,
      fileSize:   img.fileSize ?? 0,
    }));
    const stats = {};
    METRICS.forEach(({ key }) => {
      const vals = items.map(i => i[key]);
      const mean = vals.reduce((s, v) => s + v, 0) / (vals.length || 1);
      const std  = Math.sqrt(vals.reduce((s, v) => s + (v - mean) ** 2, 0) / (vals.length || 1)) || 1;
      stats[key] = { mean, std };
    });
    const scored = items.map(item => {
      const zs = {};
      METRICS.forEach(({ key }) => { zs[key] = +((item[key] - stats[key].mean) / stats[key].std).toFixed(2); });
      const maxZ = Math.max(...Object.values(zs).map(Math.abs));
      return { ...item, zs, maxZ };
    });
    setResult({ items: scored, stats });
    setRunning(false);
  }, [images]);

  if (images.length < 3) return <Info color={C.red}>Load at least 3 images to detect anomalies.</Info>;

  const outliers = result ? result.items.filter(i => i.maxZ >= threshold).sort((a, b) => b.maxZ - a.maxZ) : [];
  const normal   = result ? result.items.length - outliers.length : 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <Card>
        <Head label="ANOMALY / OUTLIER DETECTION" accent={C.red} sub="Per-metric z-score flags images that deviate unusually from the corpus average" />
        <Body>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 9, color: C.t3, fontFamily: 'JetBrains Mono,monospace' }}>SENSITIVITY (|z| ≥):</span>
            {[1.5, 2.0, 2.5, 3.0].map(v => (
              <button key={v} onClick={() => setThreshold(v)} style={{ padding: '4px 10px', border: `1px solid ${threshold === v ? C.red : C.b1}`, background: threshold === v ? C.red + '18' : 'transparent', color: threshold === v ? C.red : C.t3, fontSize: 9, borderRadius: 2, cursor: 'pointer', fontFamily: 'JetBrains Mono,monospace', fontWeight: threshold === v ? 700 : 400 }}>{v}σ</button>
            ))}
            <Btn onClick={run} color={C.red} disabled={running}>{running ? '⟳ SCANNING…' : '▶ DETECT OUTLIERS'}</Btn>
          </div>
          <div style={{ marginTop: 8, fontSize: 9, color: C.t4 }}>Checks: brightness, sharpness, contrast, file size. Z-score = how many standard deviations from corpus mean.</div>
        </Body>
      </Card>

      {result && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10 }}>
            <MB label="OUTLIERS DETECTED" value={outliers.length} color={outliers.length === 0 ? C.green : C.red}  sub={`at |z| ≥ ${threshold}σ`} />
            <MB label="NORMAL IMAGES"     value={normal}          color={C.green}                                   sub="within expected range" />
            <MB label="METRICS CHECKED"   value={METRICS.length}  color={C.cyan}                                    sub="brightness · sharpness · contrast · size" />
          </div>

          {/* Z-score scatter — one mini chart per metric */}
          <Card>
            <Head label="Z-SCORE PER METRIC — full corpus" accent={C.red} sub="Red dots = outliers beyond threshold · hover for image name" />
            <Body>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                {METRICS.map(({ key, label, color }) => {
                  const data = result.items.map((item, i) => ({
                    x: i, y: item.zs[key], name: item.name, isOut: Math.abs(item.zs[key]) >= threshold,
                  }));
                  return (
                    <div key={key}>
                      <div style={{ fontSize: 9, color, fontFamily: 'JetBrains Mono,monospace', letterSpacing: '.1em', marginBottom: 4 }}>{label}</div>
                      <ResponsiveContainer width="100%" height={130}>
                        <ScatterChart margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
                          <CartesianGrid strokeDasharray="2 2" stroke={C.b0} />
                          <XAxis dataKey="x" hide />
                          <YAxis dataKey="y" tick={{ fill: C.t4, fontSize: 7 }} tickFormatter={v => `${v > 0 ? '+' : ''}${v}`} />
                          <Tooltip content={({ active, payload }) => {
                            if (!active || !payload?.length) return null;
                            const d = payload[0]?.payload;
                            return <div style={{ background: C.surface, border: `1px solid ${C.b2}`, padding: '4px 8px', borderRadius: 3, fontSize: 9 }}><div style={{ color: d?.isOut ? C.red : C.t2 }}>{d?.name}</div><div style={{ color: C.t3 }}>z = {d?.y > 0 ? '+' : ''}{d?.y}σ</div></div>;
                          }} />
                          <Scatter data={data}>
                            {data.map((d, i) => <Cell key={i} fill={d.isOut ? C.red : color + '60'} />)}
                          </Scatter>
                        </ScatterChart>
                      </ResponsiveContainer>
                    </div>
                  );
                })}
              </div>
            </Body>
          </Card>

          {/* Outlier gallery */}
          {outliers.length > 0 ? (
            <Card>
              <Head label={`OUTLIER GALLERY — ${outliers.length} anomalies`} accent={C.red} sub="Sorted by max absolute z-score" />
              <Body style={{ padding: 8 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {outliers.map(item => (
                    <div key={item.id} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '8px 10px', background: C.deep, borderRadius: 3, border: `1px solid ${C.red}22` }}>
                      <img src={item.url} alt={item.name} style={{ width: 58, height: 58, objectFit: 'cover', borderRadius: 2, flexShrink: 0 }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 10, color: C.t1, fontFamily: 'JetBrains Mono,monospace', marginBottom: 5 }}>{item.name}</div>
                        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                          {METRICS.map(({ key, label, color }) => {
                            const z   = item.zs[key];
                            const abs = Math.abs(z);
                            const c   = abs >= threshold ? (z > 0 ? C.orange : C.cyan) : C.t4;
                            return (
                              <span key={key} style={{ fontSize: 8, padding: '2px 7px', border: `1px solid ${c}44`, color: c, borderRadius: 2, fontFamily: 'JetBrains Mono,monospace', fontWeight: abs >= threshold ? 700 : 400, whiteSpace: 'nowrap' }}>
                                {label}: {z > 0 ? '+' : ''}{z}σ
                              </span>
                            );
                          })}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <div style={{ fontFamily: 'Syne,sans-serif', fontWeight: 900, fontSize: 22, color: C.red, lineHeight: 1 }}>{item.maxZ.toFixed(1)}</div>
                        <div style={{ fontSize: 8, color: C.t4 }}>max z-score</div>
                      </div>
                    </div>
                  ))}
                </div>
              </Body>
            </Card>
          ) : (
            <Info color={C.green}>✓ No anomalies detected at {threshold}σ — all images are within the expected distribution of your corpus.</Info>
          )}
        </>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// ── 16  OCR SCAN TAB ─────────────────────────────────────────────────────────
// ═════════════════════════════════════════════════════════════════════════════

const LANGS = [['eng','English'],['fra','French'],['deu','German'],['spa','Spanish'],['por','Portuguese'],['ita','Italian'],['chi_sim','Chinese (Simplified)'],['jpn','Japanese'],['kor','Korean'],['ara','Arabic'],['hin','Hindi'],['rus','Russian']];

function OCRScanTab({ images, onImagesChange }) {
  const [scanning, setScanning] = useState(false);
  const [progress, setProgress] = useState({});
  const [lang, setLang]         = useState('eng');
  const [mode, setMode]         = useState('all'); // all | pending | single
  const [selPage, setSelPage]   = useState(0);
  const cancelRef = useRef(false);

  const runOCR = useCallback(async () => {
    setScanning(true); cancelRef.current = false;
    let targets;
    if (mode === 'single') targets = [images[selPage]].filter(Boolean);
    else if (mode === 'pending') targets = images.filter(p => p.status !== 'scanned');
    else targets = images;

    let Tesseract;
    try { Tesseract = await getTesseract(); }
    catch (e) { alert('Could not load Tesseract.js. Check your internet connection.'); setScanning(false); return; }

    for (const page of targets) {
      if (cancelRef.current) break;
      setProgress(p => ({ ...p, [page.id]: { pct: 0, status: 'Initializing…' } }));
      try {
        const src = page.processedUrl || page.url;
        const result = await Tesseract.recognize(src, lang, {
          logger: m => {
            if (m.status === 'recognizing text') setProgress(p => ({ ...p, [page.id]: { pct: Math.round(m.progress * 100), status: `Recognizing… ${Math.round(m.progress * 100)}%` } }));
            else setProgress(p => ({ ...p, [page.id]: { pct: 0, status: m.status } }));
          }
        });
        const text = result.data.text || '';
        const conf = result.data.confidence || 0;
        const entities = extractStructured(text);
        const tables   = extractTables(text);
        onImagesChange(prev => prev.map(p => p.id === page.id ? { ...p, text, confidence: conf, entities, tables, status: 'scanned' } : p));
        setProgress(p => ({ ...p, [page.id]: { pct: 100, status: 'Complete ✓' } }));
      } catch (err) {
        setProgress(p => ({ ...p, [page.id]: { pct: 0, status: `Error: ${err.message}` } }));
      }
    }
    setScanning(false);
  }, [images, mode, selPage, lang, onImagesChange]);

  const scanned = images.filter(p => p.status === 'scanned');
  const totalWords = scanned.reduce((a, p) => a + (p.text || '').split(/\s+/).filter(Boolean).length, 0);
  const avgConf = scanned.length ? +(scanned.reduce((a, p) => a + (p.confidence || 0), 0) / scanned.length).toFixed(1) : 0;

  if (images.length === 0) return <div style={{ padding: 60, textAlign: 'center', color: C.t4 }}><div style={{ fontSize: 32, opacity: .3, marginBottom: 12 }}>🔍</div>No pages loaded — go to UPLOAD first.</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10 }}>
        <MB label="PAGES LOADED"   value={images.length}       color={C.cyan} />
        <MB label="OCR COMPLETE"   value={scanned.length}      color={C.green} sub={`${(scanned.length / Math.max(images.length, 1) * 100).toFixed(0)}%`} />
        <MB label="TOTAL WORDS"    value={totalWords.toLocaleString()} color={C.violet} />
        <MB label="AVG CONFIDENCE" value={avgConf ? `${avgConf}%` : '—'} color={avgConf >= 80 ? C.green : avgConf >= 60 ? C.gold : C.red} />
      </div>

      <Card>
        <Head label="OCR CONFIGURATION" accent={C.violet} sub="Powered by Tesseract.js — 100% in-browser, your images never leave your device" />
        <Body>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div>
              <Label>LANGUAGE</Label>
              <select value={lang} onChange={e => setLang(e.target.value)} style={{ width: '100%', background: C.deep, border: `1px solid ${C.b2}`, color: C.t1, padding: '7px 10px', fontSize: 11, fontFamily: 'JetBrains Mono,monospace', borderRadius: 3, outline: 'none' }}>
                {LANGS.map(([code, name]) => <option key={code} value={code}>{name}</option>)}
              </select>
            </div>
            <div>
              <Label>SCAN TARGET</Label>
              <div style={{ display: 'flex', gap: 6 }}>
                {[['all', 'ALL PAGES'], ['pending', 'PENDING ONLY'], ['single', 'SINGLE PAGE']].map(([v, l]) => (
                  <button key={v} onClick={() => setMode(v)} style={{ flex: 1, padding: '7px 4px', border: `1px solid ${mode === v ? C.violet : C.b1}`, background: mode === v ? C.violet + '18' : 'transparent', color: mode === v ? C.violet : C.t3, fontSize: 8, borderRadius: 3, cursor: 'pointer', fontFamily: 'JetBrains Mono,monospace', fontWeight: 700 }}>{l}</button>
                ))}
              </div>
            </div>
          </div>
          {mode === 'single' && (
            <div style={{ marginTop: 12 }}>
              <Label>SELECT PAGE</Label>
              <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4 }}>
                {images.map((p, i) => <PageThumb key={p.id} img={p} selected={i === selPage} onClick={() => setSelPage(i)} size={56} />)}
              </div>
            </div>
          )}
          <div style={{ marginTop: 14, display: 'flex', gap: 10, alignItems: 'center' }}>
            <Btn onClick={runOCR} color={C.violet} disabled={scanning || images.length === 0}>{scanning ? '⟳ SCANNING…' : '▶ RUN OCR'}</Btn>
            {scanning && <Btn onClick={() => { cancelRef.current = true; }} color={C.red} small>CANCEL</Btn>}
            {scanned.length === images.length && images.length > 0 && <span style={{ fontSize: 10, color: C.green }}>✓ All pages scanned</span>}
          </div>
        </Body>
      </Card>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {images.map(page => {
          const prog = progress[page.id];
          const done = page.status === 'scanned';
          return (
            <Card key={page.id}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '12px 14px' }}>
                <div style={{ flexShrink: 0, width: 56, height: 72, background: C.deep, borderRadius: 3, overflow: 'hidden', border: `1px solid ${C.b1}` }}>
                  <img src={page.processedUrl || page.url} alt={page.name} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <div style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 10, color: C.t1, fontWeight: 700 }}>P{page.pageNum} — {page.name}</div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      {done && <ConfBadge conf={page.confidence} />}
                      <span style={{ fontSize: 8, color: done ? C.green : prog ? C.violet : C.t4, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700 }}>{done ? '✓ COMPLETE' : prog ? prog.status : 'PENDING'}</span>
                    </div>
                  </div>
                  {prog && !done && <div style={{ height: 4, background: C.b1, borderRadius: 2, marginBottom: 6, overflow: 'hidden' }}><div style={{ height: '100%', width: `${prog.pct || 5}%`, background: C.violet, borderRadius: 2, transition: 'width .3s' }} /></div>}
                  {done && (
                    <div>
                      <div style={{ height: 4, background: C.green + '33', borderRadius: 2, marginBottom: 6 }}><div style={{ height: '100%', width: '100%', background: C.green, borderRadius: 2 }} /></div>
                      <div style={{ fontSize: 10, color: C.t2, lineHeight: 1.55, maxHeight: 48, overflow: 'hidden' }}>{(page.text || '').slice(0, 180)}{(page.text || '').length > 180 && '…'}</div>
                      <div style={{ display: 'flex', gap: 12, marginTop: 6 }}>
                        <span style={{ fontSize: 9, color: C.t4, fontFamily: 'JetBrains Mono,monospace' }}>{(page.text || '').split(/\s+/).filter(Boolean).length} words</span>
                        <span style={{ fontSize: 9, color: C.t4, fontFamily: 'JetBrains Mono,monospace' }}>{page.entities.length} entities</span>
                        <span style={{ fontSize: 9, color: C.t4, fontFamily: 'JetBrains Mono,monospace' }}>{page.tables.length} tables</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// ── 14  OCR EDITOR TAB ───────────────────────────────────────────────────────
// ═════════════════════════════════════════════════════════════════════════════

function OCREditorTab({ images, onImagesChange }) {
  const [selected, setSelected] = useState(0);
  const [findVal, setFindVal]   = useState('');
  const [replVal, setReplVal]   = useState('');
  const [showFR, setShowFR]     = useState(false);
  const [zoom, setZoom]         = useState(100);

  const page = images[selected];
  const scanned = images.filter(p => p.status === 'scanned');

  const updateText = useCallback(val => {
    const entities = extractStructured(val);
    const tables   = extractTables(val);
    onImagesChange(prev => prev.map((p, i) => i === selected ? { ...p, text: val, entities, tables } : p));
  }, [selected, onImagesChange]);

  const doReplace = () => {
    if (!findVal.trim() || !page) return;
    updateText((page.text || '').replaceAll(findVal, replVal));
  };
  const doReplaceAll = () => {
    if (!findVal.trim()) return;
    onImagesChange(prev => prev.map(p => {
      if (p.status !== 'scanned') return p;
      const newText = (p.text || '').replaceAll(findVal, replVal);
      return { ...p, text: newText, entities: extractStructured(newText), tables: extractTables(newText) };
    }));
  };

  if (scanned.length === 0) return <div style={{ padding: 60, textAlign: 'center', color: C.t4 }}><div style={{ fontSize: 32, opacity: .3, marginBottom: 12 }}>✎</div>No scanned pages yet — run OCR in the SCAN tab first.</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4, alignItems: 'center' }}>
        {images.map((p, i) => <PageThumb key={p.id} img={p} selected={i === selected} onClick={() => setSelected(i)} size={60} />)}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexShrink: 0 }}>
          <button onClick={() => setShowFR(x => !x)} style={{ padding: '5px 12px', background: showFR ? C.gold + '18' : C.raised, border: `1px solid ${showFR ? C.gold : C.b1}`, color: showFR ? C.gold : C.t3, fontSize: 9, borderRadius: 3, cursor: 'pointer', fontFamily: 'JetBrains Mono,monospace', fontWeight: 700 }}>⚲ FIND & REPLACE</button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 9, color: C.t4, fontFamily: 'JetBrains Mono,monospace' }}>ZOOM</span>
            <input type="range" min={60} max={160} value={zoom} onChange={e => setZoom(+e.target.value)} style={{ width: 60, accentColor: C.cyan }} />
            <span style={{ fontSize: 9, color: C.cyan, fontFamily: 'JetBrains Mono,monospace' }}>{zoom}%</span>
          </div>
        </div>
      </div>

      {showFR && (
        <Card>
          <Head label="FIND & REPLACE" accent={C.gold} />
          <Body>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
              <div><Label>FIND</Label><input value={findVal} onChange={e => setFindVal(e.target.value)} placeholder="Text to find…" style={{ width: '100%', background: C.deep, border: `1px solid ${C.b2}`, color: C.t1, padding: '7px 10px', fontSize: 11, fontFamily: 'JetBrains Mono,monospace', borderRadius: 3, outline: 'none', boxSizing: 'border-box' }} /></div>
              <div><Label>REPLACE WITH</Label><input value={replVal} onChange={e => setReplVal(e.target.value)} placeholder="Replacement…" style={{ width: '100%', background: C.deep, border: `1px solid ${C.b2}`, color: C.t1, padding: '7px 10px', fontSize: 11, fontFamily: 'JetBrains Mono,monospace', borderRadius: 3, outline: 'none', boxSizing: 'border-box' }} /></div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Btn onClick={doReplace}    color={C.gold}>THIS PAGE</Btn>
              <Btn onClick={doReplaceAll} color={C.orange}>ALL PAGES</Btn>
            </div>
          </Body>
        </Card>
      )}

      {page && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <Card>
            <Head label={`PAGE ${page.pageNum} IMAGE`} accent={C.cyan} sub={`${page.width}×${page.height} · ${page.fileSize}KB`} right={page.confidence != null && <ConfBadge conf={page.confidence} />} />
            <Body style={{ padding: 10, maxHeight: 640, overflowY: 'auto' }}>
              <div style={{ background: C.deep, borderRadius: 3, overflow: 'auto' }}>
                <img src={page.processedUrl || page.url} alt={page.name} style={{ width: `${zoom}%`, display: 'block', transformOrigin: 'top left' }} />
              </div>
            </Body>
          </Card>
          <Card>
            <Head label="EXTRACTED TEXT — CLICK TO EDIT" accent={C.cyan}
              sub={`${(page.text || '').split(/\s+/).filter(Boolean).length} words · ${(page.text || '').split('\n').filter(Boolean).length} lines`}
              right={<button onClick={() => updateText('')} style={{ fontSize: 8, padding: '2px 8px', background: 'transparent', border: `1px solid ${C.red}33`, color: C.red, borderRadius: 2, cursor: 'pointer', fontFamily: 'JetBrains Mono,monospace' }}>CLEAR</button>} />
            <Body style={{ padding: 0 }}>
              <textarea value={page.text || ''} onChange={e => updateText(e.target.value)}
                placeholder={page.status === 'scanned' ? '(no text extracted)' : 'Run OCR in SCAN tab first'}
                style={{ width: '100%', minHeight: 540, background: C.deep, border: 'none', color: C.t1, padding: 14, fontSize: 11, fontFamily: '"Fira Code",JetBrains Mono,monospace', lineHeight: 1.75, outline: 'none', resize: 'vertical', boxSizing: 'border-box' }} />
            </Body>
          </Card>
        </div>
      )}

      {page?.entities?.length > 0 && (
        <Card>
          <Head label={`ENTITIES IN PAGE ${page.pageNum}`} accent={C.cyan} />
          <Body>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {page.entities.map(e => e.values.map((v, i) => (
                <span key={`${e.type}-${i}`} style={{ padding: '3px 10px', background: e.color + '12', border: `1px solid ${e.color}44`, borderRadius: 3, fontSize: 10, color: e.color, fontFamily: 'JetBrains Mono,monospace' }}>
                  <span style={{ opacity: .7, fontSize: 8, marginRight: 4 }}>{e.icon} {e.type}</span>{v}
                </span>
              )))}
            </div>
          </Body>
        </Card>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// ── 15  EXTRACT TAB ──────────────────────────────────────────────────────────
// ═════════════════════════════════════════════════════════════════════════════

function ExtractTab({ images }) {
  const [view, setView] = useState('entities');
  const scanned = images.filter(p => p.status === 'scanned' && p.text);
  const allText  = scanned.map(p => p.text).join('\n\n');

  const allEntities = useMemo(() => {
    const agg = {};
    scanned.forEach(page => page.entities.forEach(e => {
      if (!agg[e.type]) agg[e.type] = { ...e, values: [], pages: [] };
      e.values.forEach(v => { if (!agg[e.type].values.includes(v)) agg[e.type].values.push(v); });
      if (!agg[e.type].pages.includes(page.pageNum)) agg[e.type].pages.push(page.pageNum);
    }));
    return Object.values(agg).sort((a, b) => b.values.length - a.values.length);
  }, [scanned]);

  const allTables = useMemo(() => {
    const tbls = [];
    scanned.forEach(page => page.tables.forEach((t, i) => tbls.push({ ...t, pageNum: page.pageNum, idx: i })));
    return tbls;
  }, [scanned]);

  const keyValues = useMemo(() => {
    const kv = [];
    const kvPatterns = [/^([A-Z][a-zA-Z\s]{1,30})\s*:\s*(.{1,80})$/gm, /^([\w\s]{2,20})\s*[=:]\s*(.{1,60})$/gm];
    kvPatterns.forEach(rx => {
      const rxC = new RegExp(rx.source, rx.flags); let m;
      while ((m = rxC.exec(allText)) !== null) {
        const key = m[1].trim(), val = m[2].trim();
        if (key.length > 2 && val.length > 0 && !kv.find(k => k.key === key)) kv.push({ key, val });
      }
    });
    return kv.slice(0, 40);
  }, [allText]);

  const wordFreq = useMemo(() => {
    const STOP = new Set(['the', 'a', 'an', 'and', 'or', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'is', 'are', 'was', 'were', 'be', 'have', 'has', 'this', 'that', 'it', 'its', 'we', 'you', 'i', 'as', 'by', 'from', 'not', 'no', 'but', 'so', 'if']);
    const freq = {};
    allText.toLowerCase().replace(/[^a-z\s]/g, ' ').split(/\s+/).filter(w => w.length > 2 && !STOP.has(w)).forEach(w => { freq[w] = (freq[w] || 0) + 1; });
    return Object.entries(freq).sort((a, b) => b[1] - a[1]).slice(0, 50).map(([word, count]) => ({ word, count }));
  }, [allText]);

  const exportJSON = () => {
    usageDone('image_insights', 'report_download', 1, { format: 'json', mode: 'doc' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([JSON.stringify({ entities: allEntities, tables: allTables, keyValues, generatedAt: new Date().toISOString() }, null, 2)], { type: 'application/json' })); a.download = 'pixelmind-extracted.json'; a.click();
  };
  const exportCSV  = () => {
    usageDone('image_insights', 'report_download', 1, { format: 'csv', mode: 'doc' });
    const rows = allEntities.flatMap(e => e.values.map(v => ({ type: e.type, value: v, pages: e.pages.join(';') }))); const csv = [['type', 'value', 'pages'].join(','), ...rows.map(r => Object.values(r).map(v => `"${v}"`).join(','))].join('\n'); const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' })); a.download = 'pixelmind-entities.csv'; a.click();
  };

  if (scanned.length === 0) return <div style={{ padding: 60, textAlign: 'center', color: C.t4 }}><div style={{ fontSize: 32, opacity: .3, marginBottom: 12 }}>🔎</div>No scanned pages yet — run OCR in SCAN tab first.</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 10 }}>
        <MB label="ENTITY TYPES"    value={allEntities.length} color={C.cyan} />
        <MB label="UNIQUE VALUES"   value={allEntities.reduce((a, e) => a + e.values.length, 0)} color={C.violet} />
        <MB label="TABLES FOUND"    value={allTables.length}   color={C.gold} />
        <MB label="KEY-VALUE PAIRS" value={keyValues.length}   color={C.green} />
        <MB label="TOTAL WORDS"     value={allText.split(/\s+/).filter(Boolean).length.toLocaleString()} color={C.cyan} />
      </div>

      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        {[['entities', 'ENTITIES'], ['tables', 'TABLES'], ['keyvalue', 'KEY-VALUE'], ['terms', 'TERM CLOUD']].map(([v, l]) => (
          <button key={v} onClick={() => setView(v)} style={{ padding: '6px 14px', border: `1px solid ${view === v ? C.cyan : C.b0}`, background: view === v ? C.cyan + '15' : C.raised, color: view === v ? C.cyan : C.t2, fontSize: 9, cursor: 'pointer', borderRadius: 3, fontFamily: 'JetBrains Mono,monospace', fontWeight: view === v ? 700 : 400 }}>{l}</button>
        ))}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          <button onClick={exportJSON} style={{ padding: '5px 12px', background: C.violet + '15', border: `1px solid ${C.violet}`, color: C.violet, fontSize: 9, borderRadius: 3, cursor: 'pointer', fontFamily: 'JetBrains Mono,monospace', fontWeight: 700 }}>↓ JSON</button>
          <button onClick={exportCSV}  style={{ padding: '5px 12px', background: C.green + '15', border: `1px solid ${C.green}`, color: C.green, fontSize: 9, borderRadius: 3, cursor: 'pointer', fontFamily: 'JetBrains Mono,monospace', fontWeight: 700 }}>↓ CSV</button>
        </div>
      </div>

      {view === 'entities' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {allEntities.length === 0 && <Info>No structured entities detected. Documents with dates, amounts, emails, and invoice numbers work best.</Info>}
          {allEntities.map(e => (
            <Card key={e.type}>
              <Head label={`${e.icon} ${e.type} — ${e.values.length} UNIQUE VALUE${e.values.length !== 1 ? 'S' : ''}`} accent={e.color} sub={`Page${e.pages.length !== 1 ? 's' : ''}: ${e.pages.join(', ')}`} />
              <Body><div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>{e.values.map((v, i) => <div key={i} style={{ padding: '6px 12px', background: e.color + '10', border: `1px solid ${e.color}44`, borderRadius: 3, fontFamily: 'JetBrains Mono,monospace', fontSize: 11, color: e.color, fontWeight: 700 }}>{v}</div>)}</div></Body>
            </Card>
          ))}
        </div>
      )}
      {view === 'tables' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {allTables.length === 0 && <Info color={C.gold}>No table structures detected. Tables require ≥2 columns and rows with consistent delimiters (tabs, pipes, or aligned spacing).</Info>}
          {allTables.map((t, ti) => (
            <Card key={ti}>
              <Head label={`TABLE ${ti + 1} — PAGE ${t.pageNum}`} accent={C.gold} sub={`${t.rows.length} rows × ${Math.max(...t.rows.map(r => r.length))} columns`} />
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  {t.rows[0] && <thead><tr style={{ background: C.deep }}>{t.rows[0].map((cell, ci) => <th key={ci} style={TH}>{cell}</th>)}</tr></thead>}
                  <tbody>{t.rows.slice(1).map((row, ri) => <tr key={ri} style={{ borderBottom: `1px solid ${C.b0}`, background: ri % 2 === 0 ? 'transparent' : C.raised + '60' }}>{row.map((cell, ci) => <td key={ci} style={TD}>{cell}</td>)}</tr>)}</tbody>
                </table>
              </div>
            </Card>
          ))}
        </div>
      )}
      {view === 'keyvalue' && (
        <Card>
          <Head label="KEY-VALUE PAIRS" accent={C.green} />
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr style={{ background: C.deep }}><th style={TH}>KEY</th><th style={TH}>VALUE</th></tr></thead>
              <tbody>{keyValues.map((kv, i) => <tr key={i} style={{ borderBottom: `1px solid ${C.b0}`, background: i % 2 === 0 ? 'transparent' : C.raised + '60' }}><td style={{ ...TD, color: C.cyan, fontWeight: 700 }}>{kv.key}</td><td style={TD}>{kv.val}</td></tr>)}</tbody>
            </table>
          </div>
        </Card>
      )}
      {view === 'terms' && (
        <Card>
          <Head label="TERM FREQUENCY CLOUD" accent={C.violet} />
          <Body>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '10px 0', lineHeight: 1.8 }}>
              {wordFreq.map((w, i) => {
                const pct = w.count / wordFreq[0].count;
                return <span key={w.word} title={`${w.count} occurrences`} style={{ fontSize: Math.round(10 + pct * 22), color: PALETTE[i % PALETTE.length], opacity: .5 + pct * .5, fontWeight: pct > .5 ? 700 : 400, fontFamily: 'Syne,sans-serif', cursor: 'default' }}>{w.word}</span>;
              })}
            </div>
          </Body>
        </Card>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// ── 16  REPORT TAB — unified vision + document ───────────────────────────────
// ═════════════════════════════════════════════════════════════════════════════

const THEMES = {
  dark:  { bg:'#050a14', surface:'#0a1120', card:'#0f1826', border:'#1e293b', t1:'#e2e8f0', t2:'#b6c3d8', t3:'#8396b3', t4:'#4a6070', accent:'#22d3ee', accent2:'#a78bfa', cover:'linear-gradient(135deg,#050a14 0%,#0f1826 50%,#1a0a2e 100%)', headerBg:'#060b18' },
  light: { bg:'#f8faff', surface:'#ffffff', card:'#f1f5fd', border:'#d1daf0', t1:'#0f1826', t2:'#2d3f55', t3:'#5a7090', t4:'#9ab0c8', accent:'#0ea5e9', accent2:'#7c3aed', cover:'linear-gradient(135deg,#0f1826 0%,#1e2d4a 50%,#2d1854 100%)', headerBg:'#0f1826' },
  sepia: { bg:'#faf6f0', surface:'#fffdf8', card:'#f5efe5', border:'#d6c9b0', t1:'#2a1f0e', t2:'#4a3520', t3:'#7a6045', t4:'#a08060', accent:'#c47c20', accent2:'#8b4513', cover:'linear-gradient(135deg,#2a1f0e 0%,#4a3520 50%,#6a4530 100%)', headerBg:'#2a1f0e' },
};

function ReportTab({ images, mode, experiment }) {
  const [theme, setTheme]   = useState('dark');
  const [layout, setLayout] = useState('full');
  const [title, setTitle]   = useState(experiment?.name || 'PixelMind Analysis Report');
  const [subtitle, setSubtitle] = useState('');
  const [showThumbs, setShowThumbs]     = useState(true);
  const [showEntities, setShowEntities] = useState(true);
  const [showText, setShowText]         = useState(true);
  const [showTables, setShowTables]     = useState(true);

  const T = THEMES[theme];

  // Vision mode: compute palette for first image
  const [visionPalette, setVisionPalette] = useState(null);
  useEffect(() => {
    if (mode !== 'vision' || images.length === 0) return;
    const el = new Image(); el.src = images[0].url;
    const run = () => setVisionPalette(dominantPalette(el, 8));
    el.onload = run; if (el.complete) run();
  }, [mode, images[0]?.id]);

  // Doc mode
  const scanned = images.filter(p => p.status === 'scanned' && p.text);
  const stats = useMemo(() => docStats(scanned), [scanned]);
  const allEntities = useMemo(() => {
    const agg = {};
    scanned.forEach(p => p.entities.forEach(e => {
      if (!agg[e.type]) agg[e.type] = { ...e, values: [], pages: [] };
      e.values.forEach(v => { if (!agg[e.type].values.includes(v)) agg[e.type].values.push(v); });
      if (!agg[e.type].pages.includes(p.pageNum)) agg[e.type].pages.push(p.pageNum);
    }));
    return Object.values(agg).sort((a, b) => b.values.length - a.values.length);
  }, [scanned]);

  const exportMD = () => {
    usageDone('image_insights', 'report_download', 1, { format: 'markdown', mode });
    const lines = [`# ${title}`, subtitle && `_${subtitle}_`, `> Generated ${new Date().toLocaleDateString()} · ${images.length} ${mode === 'doc' ? 'pages' : 'images'}`, ''];
    if (mode === 'vision') {
      lines.push('## Summary', `- **Images:** ${images.length}`, `- **Avg brightness:** ${(images.reduce((s, i) => s + (i.metrics?.brightness || 0), 0) / images.length).toFixed(1)}%`, `- **Total size:** ${(images.reduce((s, i) => s + i.fileSize, 0) / 1024).toFixed(1)}MB`);
    } else {
      lines.push('## Summary', `- **Pages:** ${scanned.length}`, `- **Words:** ${stats.words.toLocaleString()}`, `- **Avg confidence:** ${stats.avgConf}%`);
      if (allEntities.length) { lines.push('', '## Extracted Data'); allEntities.forEach(e => lines.push(`**${e.icon} ${e.type}**: ${e.values.join(' · ')}`)); }
      scanned.forEach(p => { lines.push(`\n## Page ${p.pageNum}: ${p.name}\n`); lines.push('```'); lines.push(p.text || '(no text)'); lines.push('```\n'); });
    }
    const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([lines.join('\n')], { type: 'text/markdown' })); a.download = `${title.replace(/\s/g, '-')}.md`; a.click();
  };

  const isEmpty = mode === 'doc' ? scanned.length === 0 : images.length === 0;
  if (isEmpty) return <div style={{ padding: 60, textAlign: 'center', color: C.t4 }}><div style={{ fontSize: 32, opacity: .3, marginBottom: 12 }}>📄</div>{mode === 'doc' ? 'No scanned pages — run OCR first.' : 'No images loaded.'}</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Controls */}
      <Card>
        <Head label="REPORT SETTINGS" accent={C.cyan} />
        <Body>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div><Label>TITLE</Label><input value={title} onChange={e => setTitle(e.target.value)} style={{ width: '100%', background: C.deep, border: `1px solid ${C.b2}`, color: C.t1, padding: '7px 10px', fontSize: 12, fontFamily: 'inherit', borderRadius: 3, outline: 'none', boxSizing: 'border-box' }} /></div>
            <div><Label>SUBTITLE</Label><input value={subtitle} onChange={e => setSubtitle(e.target.value)} placeholder="Optional…" style={{ width: '100%', background: C.deep, border: `1px solid ${C.b2}`, color: C.t1, padding: '7px 10px', fontSize: 12, fontFamily: 'inherit', borderRadius: 3, outline: 'none', boxSizing: 'border-box' }} /></div>
          </div>
          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginBottom: 14 }}>
            <div><Label>THEME</Label><div style={{ display: 'flex', gap: 6 }}>{[['dark','◼ DARK'],['light','◻ LIGHT'],['sepia','◫ SEPIA']].map(([v, l]) => <button key={v} onClick={() => setTheme(v)} style={{ padding: '5px 10px', border: `1px solid ${theme === v ? C.cyan : C.b1}`, background: theme === v ? C.cyan + '18' : 'transparent', color: theme === v ? C.cyan : C.t3, fontSize: 9, borderRadius: 3, cursor: 'pointer', fontFamily: 'JetBrains Mono,monospace', fontWeight: theme === v ? 700 : 400 }}>{l}</button>)}</div></div>
            {mode === 'doc' && <div><Label>LAYOUT</Label><div style={{ display: 'flex', gap: 6 }}>{[['full','FULL'],['compact','COMPACT'],['magazine','MAGAZINE']].map(([v, l]) => <button key={v} onClick={() => setLayout(v)} style={{ padding: '5px 10px', border: `1px solid ${layout === v ? C.violet : C.b1}`, background: layout === v ? C.violet + '18' : 'transparent', color: layout === v ? C.violet : C.t3, fontSize: 9, borderRadius: 3, cursor: 'pointer', fontFamily: 'JetBrains Mono,monospace', fontWeight: layout === v ? 700 : 400 }}>{l}</button>)}</div></div>}
          </div>
          {mode === 'doc' && (
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
              {[['showThumbs', '📷 Page Images', showThumbs, setShowThumbs], ['showEntities', '🏷 Entities', showEntities, setShowEntities], ['showText', '📝 Full Text', showText, setShowText], ['showTables', '📊 Tables', showTables, setShowTables]].map(([k, label, checked, set]) => (
                <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                  <input type="checkbox" checked={checked} onChange={e => set(e.target.checked)} style={{ accentColor: C.cyan, width: 13, height: 13 }} />
                  <span style={{ fontSize: 10, color: C.t2 }}>{label}</span>
                </label>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={exportMD} style={{ padding: '7px 16px', background: C.cyan + '15', border: `1px solid ${C.cyan}`, color: C.cyan, fontSize: 9, borderRadius: 3, cursor: 'pointer', fontFamily: 'JetBrains Mono,monospace', fontWeight: 700 }}>↓ MARKDOWN</button>
            <button onClick={() => { usageDone('image_insights', 'report_download', 1, { format: 'print', mode }); window.print(); }} style={{ padding: '7px 16px', background: C.violet + '15', border: `1px solid ${C.violet}`, color: C.violet, fontSize: 9, borderRadius: 3, cursor: 'pointer', fontFamily: 'JetBrains Mono,monospace', fontWeight: 700 }}>🖨 PRINT / PDF</button>
          </div>
        </Body>
      </Card>

      {/* ── RENDERED REPORT ── */}
      <div style={{ background: T.bg, borderRadius: 8, overflow: 'hidden', border: `1px solid ${T.border}`, fontFamily: 'Georgia,"Times New Roman",serif' }}>
        {/* Cover */}
        <div style={{ background: T.cover, padding: '64px 56px', position: 'relative', overflow: 'hidden', minHeight: 260 }}>
          <div style={{ position: 'absolute', inset: 0, backgroundImage: 'repeating-linear-gradient(0deg,transparent,transparent 39px,rgba(255,255,255,.03) 40px),repeating-linear-gradient(90deg,transparent,transparent 39px,rgba(255,255,255,.03) 40px)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', top: 0, left: 56, right: 56, height: 3, background: `linear-gradient(90deg,${T.accent},${T.accent2},transparent)` }} />
          <div style={{ position: 'absolute', top: 28, right: 56, width: 70, height: 70, borderRight: `2px solid ${T.accent}44`, borderTop: `2px solid ${T.accent}44`, borderRadius: '0 8px 0 0' }} />
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ fontSize: 9, letterSpacing: '.25em', color: T.accent, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700, marginBottom: 16, opacity: .8 }}>PIXELMIND {mode === 'doc' ? 'DOCUMENT INTELLIGENCE' : 'IMAGE ANALYSIS'} REPORT</div>
            <h1 style={{ fontFamily: 'Syne,sans-serif', fontWeight: 900, fontSize: title.length < 20 ? 38 : title.length < 40 ? 30 : 24, color: '#fff', margin: '0 0 12px', lineHeight: 1.15 }}>{title}</h1>
            {subtitle && <p style={{ fontSize: 14, color: T.t2, margin: '0 0 28px', fontStyle: 'italic' }}>{subtitle}</p>}
            <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap', marginTop: 28 }}>
              {mode === 'vision' ? [
                [images.length, 'IMAGES'],
                [`${(images.reduce((s, i) => s + (i.metrics?.brightness || 0), 0) / images.length).toFixed(0)}%`, 'AVG BRIGHTNESS'],
                [`${(images.reduce((s, i) => s + i.fileSize, 0) / 1024).toFixed(1)}MB`, 'TOTAL SIZE'],
                [new Date().toLocaleDateString('en', { year: 'numeric', month: 'short', day: 'numeric' }), 'GENERATED'],
              ] : [
                [stats.pages, 'PAGES'],
                [stats.words.toLocaleString(), 'WORDS'],
                [stats.chars.toLocaleString(), 'CHARACTERS'],
                [`${stats.avgConf}%`, 'AVG CONFIDENCE'],
                [`${stats.readTime} min`, 'READ TIME'],
              ].map(([val, label]) => (
                <div key={label}>
                  <div style={{ fontSize: 22, fontFamily: 'Syne,sans-serif', fontWeight: 800, color: T.accent, lineHeight: 1 }}>{val}</div>
                  <div style={{ fontSize: 8, letterSpacing: '.12em', color: T.t3, marginTop: 4, fontFamily: 'JetBrains Mono,monospace' }}>{label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Vision: image grid + palette */}
        {mode === 'vision' && (
          <>
            {images.length === 1 && visionPalette && (
              <div style={{ padding: '40px 56px', borderBottom: `1px solid ${T.border}`, background: T.surface }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 32 }}>
                  <div>
                    <div style={{ fontSize: 9, letterSpacing: '.18em', color: T.accent, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700, marginBottom: 12 }}>IMAGE</div>
                    <img src={images[0].url} alt={images[0].name} style={{ width: '100%', borderRadius: 6, boxShadow: '0 8px 32px rgba(0,0,0,.5)', border: `1px solid ${T.border}` }} />
                    <div style={{ display: 'flex', height: 20, borderRadius: 3, overflow: 'hidden', marginTop: 10 }}>
                      {visionPalette.map((c, i) => <div key={i} style={{ flex: c.pct, background: c.hex }} />)}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 9, letterSpacing: '.18em', color: T.accent, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700, marginBottom: 12 }}>METRICS</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 20 }}>
                      {[['BRIGHTNESS', `${images[0].metrics?.brightness}%`, C.gold], ['CONTRAST', `${images[0].metrics?.contrast}%`, C.violet], ['SHARPNESS', images[0].metrics?.sharpness, C.cyan], ['MEGAPIXELS', +(images[0].width * images[0].height / 1e6).toFixed(2), C.green]].map(([l, v, c]) => <MB key={l} label={l} value={v} color={c} />)}
                    </div>
                    <div style={{ fontSize: 9, letterSpacing: '.18em', color: T.accent2, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700, marginBottom: 10 }}>DOMINANT PALETTE</div>
                    {visionPalette.slice(0, 6).map((c, i) => <Swatch key={i} hex={c.hex} pct={c.pct} label={`HSL(${c.hsl.h}°,${c.hsl.s}%,${c.hsl.l}%)`} />)}
                  </div>
                </div>
              </div>
            )}
            {images.length > 1 && (
              <div style={{ padding: '40px 56px', borderBottom: `1px solid ${T.border}`, background: T.surface }}>
                <div style={{ fontSize: 9, letterSpacing: '.18em', color: T.accent, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700, marginBottom: 16 }}>IMAGE COLLECTION — {images.length} IMAGES</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {images.slice(0, 80).map(im => <div key={im.id} style={{ width: 64, height: 64, borderRadius: 3, overflow: 'hidden', border: `1px solid ${T.border}` }}><img src={im.url} alt={im.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /></div>)}
                  {images.length > 80 && <div style={{ width: 64, height: 64, background: T.card, border: `1px solid ${T.border}`, borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: T.t4 }}>+{images.length - 80}</div>}
                </div>
              </div>
            )}
          </>
        )}

        {/* Doc: entities summary + per-page */}
        {mode === 'doc' && (
          <>
            {showEntities && allEntities.length > 0 && (
              <div style={{ padding: '40px 56px', borderBottom: `1px solid ${T.border}`, background: T.surface }}>
                <div style={{ fontSize: 9, letterSpacing: '.22em', color: T.accent, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700, marginBottom: 20 }}>EXTRACTED INTELLIGENCE</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(260px,1fr))', gap: 14 }}>
                  {allEntities.slice(0, 8).map(e => (
                    <div key={e.type} style={{ background: T.card, border: `1px solid ${e.color}33`, borderLeft: `3px solid ${e.color}`, borderRadius: 4, padding: '12px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                        <span style={{ fontSize: 16 }}>{e.icon}</span>
                        <span style={{ fontSize: 9, letterSpacing: '.12em', color: e.color, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700 }}>{e.type}</span>
                        <span style={{ marginLeft: 'auto', fontSize: 8, color: T.t4, fontFamily: 'JetBrains Mono,monospace' }}>{e.values.length} found</span>
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {e.values.slice(0, 5).map((v, i) => <span key={i} style={{ fontSize: 10, padding: '3px 8px', background: e.color + '18', border: `1px solid ${e.color}44`, borderRadius: 3, color: e.color, fontFamily: 'JetBrains Mono,monospace', fontWeight: 600 }}>{v}</span>)}
                        {e.values.length > 5 && <span style={{ fontSize: 9, color: T.t4, padding: '3px 0' }}>+{e.values.length - 5} more</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {scanned.map((page, pi) => (
              <div key={page.id} style={{ borderBottom: `1px solid ${T.border}`, background: pi % 2 === 0 ? T.surface : T.bg }}>
                <div style={{ background: T.headerBg, padding: '14px 56px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${T.border}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <div style={{ width: 30, height: 30, borderRadius: '50%', background: `linear-gradient(135deg,${T.accent},${T.accent2})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, color: '#fff', fontFamily: 'Syne,sans-serif', flexShrink: 0 }}>{page.pageNum}</div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#fff', fontFamily: 'Syne,sans-serif' }}>{page.name}</div>
                      <div style={{ fontSize: 9, color: T.t4, fontFamily: 'JetBrains Mono,monospace', marginTop: 2 }}>{(page.text || '').split(/\s+/).filter(Boolean).length} words · {page.entities.length} entities</div>
                    </div>
                  </div>
                  {page.confidence != null && (
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 20, fontWeight: 800, fontFamily: 'Syne,sans-serif', color: page.confidence >= 80 ? '#4ade80' : page.confidence >= 60 ? '#fbbf24' : '#f87171', lineHeight: 1 }}>{page.confidence.toFixed(0)}%</div>
                      <div style={{ fontSize: 8, color: T.t4, fontFamily: 'JetBrains Mono,monospace' }}>CONFIDENCE</div>
                    </div>
                  )}
                </div>
                <div style={{ padding: '28px 56px' }}>
                  {showThumbs && layout !== 'compact' && <div style={{ marginBottom: 20, textAlign: layout === 'magazine' ? 'left' : 'center' }}><img src={page.processedUrl || page.url} alt={page.name} style={{ maxWidth: layout === 'magazine' ? 280 : '100%', maxHeight: 320, borderRadius: 6, boxShadow: '0 6px 24px rgba(0,0,0,.35)', border: `1px solid ${T.border}`, objectFit: 'contain', float: layout === 'magazine' ? 'left' : 'none', marginRight: layout === 'magazine' ? 28 : 0 }} /></div>}
                  {showEntities && page.entities.length > 0 && (
                    <div style={{ marginBottom: 16, padding: '12px 14px', background: T.card, borderRadius: 4, border: `1px solid ${T.border}`, clear: 'none' }}>
                      <div style={{ fontSize: 8, letterSpacing: '.18em', color: T.t4, fontFamily: 'JetBrains Mono,monospace', marginBottom: 8 }}>ENTITIES</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {page.entities.map(e => e.values.map((v, i) => <span key={`${e.type}-${i}`} style={{ fontSize: 10, padding: '3px 9px', background: e.color + '12', border: `1px solid ${e.color}44`, borderRadius: 3, color: e.color, fontFamily: 'JetBrains Mono,monospace', fontWeight: 600 }}>{e.icon} {e.type}: {v}</span>))}
                      </div>
                    </div>
                  )}
                  {showTables && page.tables.map((tbl, ti) => (
                    <div key={ti} style={{ marginBottom: 16, overflowX: 'auto', clear: 'both' }}>
                      <div style={{ fontSize: 8, letterSpacing: '.15em', color: T.t4, fontFamily: 'JetBrains Mono,monospace', marginBottom: 6 }}>TABLE {ti + 1}</div>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                        {tbl.rows[0] && <thead><tr style={{ background: T.card }}>{tbl.rows[0].map((c, ci) => <th key={ci} style={{ padding: '6px 10px', textAlign: 'left', fontSize: 9, color: T.t3, borderBottom: `1px solid ${T.border}`, fontFamily: 'JetBrains Mono,monospace' }}>{c}</th>)}</tr></thead>}
                        <tbody>{tbl.rows.slice(1).map((row, ri) => <tr key={ri} style={{ borderBottom: `1px solid ${T.border}` }}>{row.map((c, ci) => <td key={ci} style={{ padding: '6px 10px', color: T.t2, fontFamily: 'JetBrains Mono,monospace', fontSize: 10 }}>{c}</td>)}</tr>)}</tbody>
                      </table>
                    </div>
                  ))}
                  {showText && page.text && (
                    <div style={{ background: T.card, borderRadius: 4, border: `1px solid ${T.border}`, overflow: 'hidden', clear: 'both' }}>
                      <div style={{ padding: '6px 14px', background: T.bg, borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: 8, letterSpacing: '.15em', color: T.t4, fontFamily: 'JetBrains Mono,monospace' }}>EXTRACTED TEXT</span>
                        <span style={{ fontSize: 8, color: T.t4, fontFamily: 'JetBrains Mono,monospace' }}>{(page.text || '').split(/\s+/).filter(Boolean).length} words</span>
                      </div>
                      <div style={{ padding: '14px 20px', fontFamily: '"Fira Code",JetBrains Mono,monospace', fontSize: layout === 'compact' ? 9.5 : 11, color: T.t2, lineHeight: 1.85, whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: layout === 'compact' ? 200 : undefined, overflow: layout === 'compact' ? 'hidden' : 'visible' }}>{page.text}</div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </>
        )}

        {/* Footer */}
        <div style={{ padding: '20px 56px', background: T.headerBg, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: `1px solid ${T.border}` }}>
          <div style={{ fontSize: 9, color: T.t4, fontFamily: 'JetBrains Mono,monospace' }}>ai.degenden.xyz · PIXELMIND</div>
          <div style={{ fontSize: 9, color: T.t4, fontFamily: 'JetBrains Mono,monospace' }}>{mode === 'doc' ? `${scanned.length} PAGES · ${stats.words.toLocaleString()} WORDS · ${stats.avgConf}% CONFIDENCE` : `${images.length} IMAGES · 100% IN-BROWSER`}</div>
        </div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// ── 17  ROOT COMPONENT ───────────────────────────────────────────────────────
// ═════════════════════════════════════════════════════════════════════════════

const TABS = [
  { id: 'upload',   icon: '⬆',  label: 'UPLOAD',   color: C.cyan,   modes: ['vision', 'doc'] },
  { id: 'inspect',  icon: '🔬', label: 'INSPECT',  color: C.cyan,   modes: ['vision'] },
  { id: 'process',  icon: '⚡', label: 'PROCESS',  color: C.orange, modes: ['vision', 'doc'] },
  { id: 'detect',   icon: '🎯', label: 'DETECT',   color: C.violet, modes: ['vision'] },
  { id: 'discover', icon: '🧠', label: 'DISCOVER', color: C.green,  modes: ['vision'] },
  { id: 'cv_filters', icon: '◈', label: 'CV FILTERS', color: C.cyan, modes: ['vision'] },
  { id: 'cv_edges',   icon: '⬟', label: 'CV EDGES',   color: C.green, modes: ['vision'] },
  { id: 'cv_morph',   icon: '◉', label: 'CV MORPH',   color: C.violet, modes: ['vision'] },
  { id: 'cv_freq',    icon: '⬡', label: 'CV FREQ',    color: C.gold, modes: ['vision'] },
  { id: 'cv_color',   icon: '◎', label: 'CV COLOR',   color: C.orange, modes: ['vision'] },
  { id: 'cv_feat',    icon: '◐', label: 'CV FEAT',    color: C.red, modes: ['vision'] },
  { id: 'cv_seg',     icon: '⬢', label: 'CV SEG',     color: C.green, modes: ['vision'] },
  { id: 'cv_neural',  icon: '◫', label: 'CV NEURAL',  color: C.pink, modes: ['vision'] },
  { id: 'ocr',      icon: '📡', label: 'OCR',      color: C.violet, modes: ['doc'] },
  { id: 'report',   icon: '📄', label: 'REPORT',   color: C.green,  modes: ['vision', 'doc'] },
];

function SuiteTabBar({ items, active, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 4, background: C.deep, border: `1px solid ${C.b1}`, borderRadius: 3, padding: 4, marginBottom: 12, overflowX: 'auto' }}>
      {items.map(item => {
        const on = active === item.id;
        return (
          <button
            key={item.id}
            onClick={() => onChange(item.id)}
            style={{
              padding: '6px 12px',
              border: `1px solid ${on ? item.color : 'transparent'}`,
              background: on ? item.color + '18' : 'transparent',
              color: on ? item.color : C.t3,
              fontSize: 9,
              cursor: 'pointer',
              borderRadius: 2,
              fontFamily: 'JetBrains Mono,monospace',
              whiteSpace: 'nowrap',
              fontWeight: on ? 700 : 400,
              letterSpacing: '.06em',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            <span style={{ fontSize: 10 }}>{item.icon}</span>
            {item.label}
          </button>
        );
      })}
    </div>
  );
}

function InspectSuiteTab(props) {
  const items = [
    { id: 'inspect', icon: '🔬', label: 'INSPECT', color: C.cyan },
    { id: 'palette', icon: '🎨', label: 'PALETTE', color: C.gold },
  ];
  const [sub, setSub] = useState('inspect');

  return (
    <>
      <SuiteTabBar items={items} active={sub} onChange={setSub} />
      {sub === 'inspect' ? <InspectTab {...props} /> : <PaletteTab {...props} />}
    </>
  );
}

function ProcessSuiteTab(props) {
  const items = props.mode === 'doc'
    ? [{ id: 'enhance', icon: '⚡', label: 'ENHANCE', color: C.orange }]
    : [
        { id: 'enhance', icon: '⚡', label: 'ENHANCE', color: C.orange },
        { id: 'transform', icon: '🔁', label: 'TRANSFORM', color: C.pink },
      ];
  const [sub, setSub] = useState('enhance');

  useEffect(() => {
    if (!items.find(i => i.id === sub)) setSub(items[0].id);
  }, [props.mode, items, sub]);

  return (
    <>
      <SuiteTabBar items={items} active={sub} onChange={setSub} />
      {sub === 'transform' ? <TransformTab {...props} /> : <EnhanceTab {...props} />}
    </>
  );
}

function DetectSuiteTab(props) {
  const items = [
    { id: 'detect', icon: '🎯', label: 'DETECT', color: C.violet },
    { id: 'compare', icon: '⚖', label: 'COMPARE', color: C.cyan },
  ];
  const [sub, setSub] = useState('detect');

  return (
    <>
      <SuiteTabBar items={items} active={sub} onChange={setSub} />
      {sub === 'compare' ? <CompareTab {...props} /> : <DetectTab {...props} />}
    </>
  );
}

function DiscoverSuiteTab(props) {
  const items = [
    { id: 'cluster', icon: '🧠', label: 'CLUSTER', color: C.violet },
    { id: 'similar', icon: '🔍', label: 'SIMILAR', color: C.green },
    { id: 'quality', icon: '🩺', label: 'QUALITY', color: C.green },
    { id: 'embed', icon: '🗺', label: 'ATLAS', color: C.violet },
    { id: 'anomaly', icon: '⚠', label: 'ANOMALY', color: C.red },
  ];
  const [sub, setSub] = useState('cluster');

  return (
    <>
      <SuiteTabBar items={items} active={sub} onChange={setSub} />
      {sub === 'cluster' && <ClusterTab {...props} />}
      {sub === 'similar' && <SimilarTab {...props} />}
      {sub === 'quality' && <QualityTab {...props} />}
      {sub === 'embed' && <EmbedTab {...props} />}
      {sub === 'anomaly' && <AnomalyTab {...props} />}
    </>
  );
}

function OCRSuiteTab(props) {
  const items = [
    { id: 'ocrscan', icon: '📡', label: 'OCR SCAN', color: C.violet },
    { id: 'ocredit', icon: '✎', label: 'EDITOR', color: C.cyan },
    { id: 'extract', icon: '🏷', label: 'EXTRACT', color: C.green },
  ];
  const [sub, setSub] = useState('ocrscan');

  return (
    <>
      <SuiteTabBar items={items} active={sub} onChange={setSub} />
      {sub === 'ocrscan' && <OCRScanTab {...props} />}
      {sub === 'ocredit' && <OCREditorTab {...props} />}
      {sub === 'extract' && <ExtractTab {...props} />}
    </>
  );
}

export default function PixelMind({ experiment, onUpdate }) {
  const [mode, setMode]   = useState('vision'); // vision | doc
  const [tab, setTab]     = useState('upload');
  const [images, setImages] = useState(() => experiment?.pixelMindImages || []);

  const onImagesChange = useCallback(updater => {
    setImages(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      onUpdate?.({ pixelMindImages: next });
      return next;
    });
  }, [onUpdate]);

  const onModeChange = useCallback(m => {
    setMode(m);
    setTab('upload');
  }, []);

  const visibleTabs = TABS.filter(t => t.modes.includes(mode));

  // Auto-switch tab if current tab not available in new mode
  useEffect(() => {
    if (!visibleTabs.find(t => t.id === tab)) setTab('upload');
  }, [mode]);

  // Usage step tracking: follow the currently visible tabs for the active mode.
  useEffect(() => {
    if (!images || images.length === 0) return;
    const workflowSteps = usageWorkflowFromTabs(visibleTabs);
    if (workflowSteps.includes(tab) && tab !== 'upload') {
      usageStep('image_insights', tab, { mode, images: images.length }, workflowSteps);
    }
  }, [tab, mode, images?.length]);

  const props = { images, onImagesChange, mode, onModeChange, experiment };
  const cvSeedSrc = images?.[0]?.processedUrl || images?.[0]?.url || null;

  const renderTab = () => {
    switch (tab) {
      case 'upload':    return <UploadTab    {...props} />;
      case 'inspect':   return <InspectSuiteTab {...props} />;
      case 'process':   return <ProcessSuiteTab {...props} />;
      case 'detect':    return <DetectSuiteTab {...props} />;
      case 'discover':  return <DiscoverSuiteTab {...props} />;
      case 'cv_filters': return <FiltersLab seedImageSrc={cvSeedSrc} hideUpload />;
      case 'cv_edges':   return <EdgeLab seedImageSrc={cvSeedSrc} hideUpload />;
      case 'cv_morph':   return <MorphologyLab seedImageSrc={cvSeedSrc} hideUpload />;
      case 'cv_freq':    return <FrequencyLab seedImageSrc={cvSeedSrc} hideUpload />;
      case 'cv_color':   return <ColorSpaceLab seedImageSrc={cvSeedSrc} hideUpload />;
      case 'cv_feat':    return <FeaturesLab seedImageSrc={cvSeedSrc} hideUpload />;
      case 'cv_seg':     return <SegmentationLab seedImageSrc={cvSeedSrc} hideUpload />;
      case 'cv_neural':  return <NeuralCVLab seedImageSrc={cvSeedSrc} />;
      case 'ocr':       return <OCRSuiteTab {...props} />;
      case 'report':    return <ReportTab    {...props} />;
      default:          return null;
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 0 }}>
      {/* Header bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 18px', background: C.deep, borderBottom: `1px solid ${C.b1}`, flexShrink: 0 }}>
        <div>
          <div style={{ fontFamily: 'Syne,sans-serif', fontWeight: 900, fontSize: 16, color: C.t1, letterSpacing: '-.02em', lineHeight: 1 }}>PixelMind</div>
          <div style={{ fontSize: 8, color: C.t4, fontFamily: 'JetBrains Mono,monospace', letterSpacing: '.12em', marginTop: 2 }}>Upload images. See everything.</div>
        </div>
        <ModeBadge mode={mode} />
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, fontSize: 9, color: C.t4, fontFamily: 'JetBrains Mono,monospace' }}>
          <span>{images.length} {mode === 'doc' ? 'page' : 'image'}{images.length !== 1 ? 's' : ''}</span>
          {mode === 'doc' && images.filter(p => p.status === 'scanned').length > 0 && <span>· {images.filter(p => p.status === 'scanned').length} scanned</span>}
          <span style={{ opacity: .5 }}>· 100% in-browser</span>
        </div>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 0, background: C.surface, borderBottom: `1px solid ${C.b1}`, flexShrink: 0, overflowX: 'auto' }}>
        {visibleTabs.map(t => {
          const active = tab === t.id;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              padding: '9px 14px', border: 'none', background: active ? C.raised : 'transparent',
              borderBottom: `2px solid ${active ? t.color : 'transparent'}`,
              color: active ? t.color : C.t3, fontSize: 9, cursor: 'pointer',
              fontFamily: 'JetBrains Mono,monospace', fontWeight: active ? 700 : 400,
              letterSpacing: '.08em', whiteSpace: 'nowrap', transition: 'all .12s',
              display: 'flex', alignItems: 'center', gap: 5,
            }}>
              <span style={{ fontSize: 11 }}>{t.icon}</span> {t.label}
            </button>
          );
        })}
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 18px' }}>
        {renderTab()}
      </div>
    </div>
  );
}
